/**
 * /projects/:projectId/pipelines/:pipelineId — the pipeline page.
 *
 * Two tabs (n8n-style) under a shared header (Run / Stop / Versions):
 * - **Workflow** — the canvas editor.
 * - **Execution** — run history + the selected run's result (status, timing,
 *   Display output).
 *
 * Design rule: the canvas is idiot-proof — the user cannot wire an invalid
 * graph. One `>` handle = one input; a port accepts only one wire; triggers
 * have no input and Display has no output; self-links and cycles are rejected.
 *
 * - Autosaves the draft silently (debounced; NOT audited).
 * - Run executes the draft and shows per-node status + per-port Display output.
 * - Versions: save the draft as an audited version; switch back to any version.
 * - Palette grouped by category (collapsed by default). Left/right panels are
 *   resizable. Read-only unless the pipeline is in Development.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addEdge,
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Accordion,
  ActionIcon,
  Alert,
  Anchor,
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  Code,
  CopyButton,
  Divider,
  Group,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconPencil,
  IconShare,
  IconLayoutDashboard,
  IconChevronDown,
  IconCopy,
  IconDeviceFloppy,
  IconDots,
  IconDownload,
  IconHistory,
  IconListDetails,
  IconTable,
  IconVariable,
  IconPlayerPlay,
  IconPlayerStop,
  IconPlus,
  IconRefresh,
  IconSitemap,
  IconTerminal2,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  getBlobWatch,
  getEditorState,
  getPipeline,
  getSchedule,
  getVersion,
  listRuns,
  listVersions,
  runPipeline,
  saveVersion,
  renamePipeline,
  stopPipeline,
  stopRun,
  setPipelineSolutionPage,
  transitionPipelineLifecycle,
  type BlobWatchOut,
  type RunOut,
  type RunSummaryOut,
  type ScheduleOut,
  type VersionListOut,
} from "@/shared/api/projects";
import { statusColor } from "@/shared/ui/palette";
import { promptForReason } from "@/shared/ui/promptForReason";
import { useEffectiveRoles } from "@/features/auth/RolePreviewContext";
import { useMe } from "@/features/users/useMe";
import { toastApiError, toastSuccess, toastWarning } from "@/shared/ui/toast";
import { listConnections } from "@/shared/api/integration";
import { listRunApprovals, type ApprovalOut } from "@/shared/api/approvals";
import {
  blobCreateContainer,
  blobListContainers,
  blobListFiles,
  veevaDescribeObject,
  type FieldDefinition,
  veevaListObjects,
  veevaQuery,
} from "@/shared/api/integration-browse";
import { VeevaQueryBuilder } from "@/features/integration/VeevaQueryBuilder";
import { CaraSourceConfig } from "./CaraSourceConfig";
import { DatabaseSourceConfig } from "./DatabaseSourceConfig";
import type { DaqParameter } from "./graph";
import { asNoteNode, fromReactFlow, toReactFlow, withArrows, type PipelineGraph } from "./graph";
import { computeInputColumns, upstreamVeeva } from "./columns";
import {
  CATEGORY_COLOR,
  CATEGORY_LABEL,
  CATEGORY_ORDER,
  DOCUMENT_CREATE_FIELDS,
  NODE_BY_KIND,
  NODE_TYPES,
  isNoteKind,
} from "./nodeRegistry";
import { PipelineNode } from "./PipelineNode";
import { ApprovalConfig } from "./ApprovalConfig";
import { aiWriteViolations } from "./aiGuard";
import { PredictMetadataConfig } from "./PredictMetadataConfig";
import { CompareConfig, ReconcileConfig } from "./CompareConfig";
import { DocumentClassificationPicker } from "./DocumentClassificationPicker";
import { ConnectionRefSelect } from "./ConnectionRefSelect";
import { useConnectionRef } from "./useConnectionRef";
import { LookupConfig } from "./LookupConfig";
import { MappingSheet, type Mapping } from "./MappingSheet";
import { ParametersPanel, RunParametersModal } from "./ParametersPanel";
import { UndoConfig } from "./UndoConfig";
import { ValidateConfig } from "./ValidateConfig";
import { RunPipelineConfig } from "./RunPipelineConfig";
import { RunLog } from "./RunLog";
import { S3SinkConfig } from "./S3SinkConfig";
import { S3SourceConfig } from "./S3SourceConfig";
import { S3TriggerConfig } from "./S3TriggerConfig";
import { BlobSourceConfig } from "./BlobSourceConfig";
import { DbSnapshotConfig } from "./DbSnapshotConfig";
import {
  ArchiveDatasetSinkConfig,
  ArchiveEnsureContainerConfig,
  ArchiveReviewConfig,
  ArchiveSinkConfig,
  ArchiveSourceConfig,
} from "./ArchiveNodeConfigs";
import { RenameModal } from "@/features/projects/RenameModal";
import { PublishToSolutionModal } from "./PublishToSolutionModal";
import { ShareDialog } from "@/features/sharing/ShareDialog";
import { NoteInspector, NoteNode, type NotePatch } from "./NoteNode";

const nodeTypes = { pipelineNode: PipelineNode, noteNode: NoteNode };

/** Run statuses that mean "the server still owes an answer". A run is queued
 *  the moment it is asked for and claimed by a worker a moment later; `waiting`
 *  is a migration paused on a child run or an approval — still in flight, just
 *  not being worked on; `cancelling` has been asked to stop but has not yet. */
const IN_FLIGHT = new Set(["queued", "running", "waiting", "cancelling"]);

function newNodeId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `n${Date.now()}`;
}

function sigOf(nodes: Node[], edges: Edge[], parameters: DaqParameter[] = []): string {
  return JSON.stringify(fromReactFlow(nodes, edges, parameters));
}

/** Would adding source→target create a cycle? (Is `source` reachable from
 *  `target` along existing edges?) */
function wouldCreateCycle(edges: Edge[], source: string, target: string): boolean {
  const adj = new Map<string, string[]>();
  for (const e of edges) {
    const out = adj.get(e.source) ?? [];
    out.push(e.target);
    adj.set(e.source, out);
  }
  const stack = [target];
  const seen = new Set<string>();
  while (stack.length) {
    const n = stack.pop()!;
    if (n === source) return true;
    if (seen.has(n)) continue;
    seen.add(n);
    for (const nxt of adj.get(n) ?? []) stack.push(nxt);
  }
  return false;
}

type DisplayResult = { port: string; data: unknown };

function ResizeHandle({ onDrag }: { onDrag: (dx: number) => void }) {
  const startX = useRef(0);
  return (
    <Box
      onPointerDown={(e) => {
        startX.current = e.clientX;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!(e.buttons & 1)) return;
        const dx = e.clientX - startX.current;
        if (dx !== 0) {
          onDrag(dx);
          startX.current = e.clientX;
        }
      }}
      style={{ width: 6, cursor: "col-resize", alignSelf: "stretch", flex: "0 0 auto" }}
    />
  );
}

function CanvasInner({ projectId, pipelineId }: { projectId: string; pipelineId: string }) {
  const getToken = useAccessToken();
  const { screenToFlowPosition } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [editable, setEditable] = useState(true);
  const [lifecycle, setLifecycle] = useState("development");
  // Legal next states, served by the API rather than re-derived here — the
  // state machine is the backend's, and a second copy of it in the SPA would
  // eventually disagree with it.
  const [nextStates, setNextStates] = useState<string[]>([]);
  const [transitioning, setTransitioning] = useState(false);
  const { data: me } = useMe();
  const effectiveRoles = useEffectiveRoles(me?.roles ?? []);
  const isAdministrator = effectiveRoles.includes("administrator");
  const isDeveloperPlus = isAdministrator || effectiveRoles.includes("developer");
  // Pipeline name — shown in the header and used as the reason on a
  // create-container call. The project name went with the Veeva node's
  // blob-destination preview, which no longer exists.
  const [pipelineName, setPipelineName] = useState("");
  const [pipelineDescription, setPipelineDescription] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [sharing, setSharing] = useState(false);
  // "Show results in" (ADR-0032): the Solution page this pipeline's runs appear
  // on. Offered to Developer+ only; the backend enforces role and ownership.
  const [solutionPage, setSolutionPage] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  // Who owns it, so the share dialog knows whether this person may grant.
  const [ownerUserId, setOwnerUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [stoppingRun, setStoppingRun] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [saving, setSaving] = useState(false);
  // Whether the canvas has edits not yet persisted to a saved version. There is
  // NO draft — unsaved edits live only here and are lost on navigation.
  const [dirty, setDirty] = useState(false);
  const [versions, setVersions] = useState<VersionListOut["items"]>([]);
  // The saved version currently loaded as the editing base (null = a brand-new
  // pipeline with nothing saved yet). Editing + Save/Run creates the NEXT
  // version; the old ones stay immutable.
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  // Column awareness for transform inspectors: sources report their columns
  // after a Test (`sourceColumns`); Set nodes with a tabular constant add their
  // keys. Transform pickers take these as *suggestions* but still allow
  // free-typed column names (v1: a flat union across the graph, not
  // per-node-accurate — forward propagation is a later refinement).
  const [sourceColumns, setSourceColumns] = useState<Record<string, string[]>>({});
  const handleColumns = useCallback((nodeId: string, cols: string[]) => {
    setSourceColumns((prev) => ({ ...prev, [nodeId]: cols }));
  }, []);
  // Per-node input columns: propagate each node's output columns forward
  // (source Test previews + Set constants → folded through each transform), so
  // a node's inspector shows the columns that actually arrive there.
  const inputColsByNode = useMemo(
    () => computeInputColumns(nodes, edges, sourceColumns),
    [nodes, edges, sourceColumns],
  );
  // Declared on the pipeline and carried in its saved graph. Kept beside the
  // nodes rather than inside one: a parameter belongs to the pipeline, and a
  // node that owned it would be a node you could not delete.
  const [parameters, setParameters] = useState<DaqParameter[]>([]);
  // Opened only when there is something to ask for, so Run stays one click for
  // the pipelines that declare nothing — which is most of them.
  const [askingParameters, setAskingParameters] = useState(false);
  const [schedule, setSchedule] = useState<ScheduleOut | null>(null);
  const [blobWatch, setBlobWatch] = useState<BlobWatchOut | null>(null);
  // `?tab=execution&run=<id>` opens the Execution tab on that run — the link a
  // Solution page (e.g. eTMF intake) uses for "Open execution details".
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(() =>
    searchParams.get("tab") === "execution" ? "execution" : "workflow",
  );
  const [runs, setRuns] = useState<RunSummaryOut[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(() => searchParams.get("run"));
  const [paletteWidth, setPaletteWidth] = useState(220);
  const [inspectorWidth, setInspectorWidth] = useState(300);
  // A parsed, shape-valid import awaiting the user's replace-confirm (null = none).
  const [pendingImport, setPendingImport] = useState<{
    graph: PipelineGraph;
    filename: string;
  } | null>(null);

  const loadedRef = useRef(false);
  const lastSavedSigRef = useRef("");
  const lastRunIdRef = useRef<string | null>(null);
  // Which run's outcome has already been reported, so following a run does not
  // toast the same ending on every poll.
  const toldAboutRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Set for the duration of a Ctrl/Cmd-drag so the duplicate is created once.
  const duplicatingRef = useRef(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  // A panel can grow up to (container width − the other panel − a small canvas
  // gutter), so on a big screen the panels can get correspondingly large.
  const containerW = () =>
    containerRef.current?.offsetWidth ?? (typeof window !== "undefined" ? window.innerWidth : 1600);
  const clampPalette = (w: number) =>
    Math.min(Math.max(200, containerW() - inspectorWidth - 60), Math.max(120, w));
  const clampInspector = (w: number) =>
    Math.min(Math.max(200, containerW() - paletteWidth - 60), Math.max(140, w));

  const refreshVersions = useCallback(async () => {
    try {
      const token = await getToken();
      setVersions((await listVersions(token, pipelineId)).items);
    } catch {
      /* non-fatal for the editor */
    }
  }, [getToken, pipelineId]);

  const refreshSchedule = useCallback(async () => {
    try {
      const token = await getToken();
      setSchedule(await getSchedule(token, pipelineId));
    } catch {
      /* non-fatal for the editor */
    }
  }, [getToken, pipelineId]);

  const refreshBlobWatch = useCallback(async () => {
    try {
      const token = await getToken();
      setBlobWatch(await getBlobWatch(token, pipelineId));
    } catch {
      /* non-fatal for the editor */
    }
  }, [getToken, pipelineId]);

  const refreshRuns = useCallback(async () => {
    try {
      const token = await getToken();
      const items = (await listRuns(token, pipelineId)).items;
      setRuns(items);
      // Keep the current selection if it still exists; else show the latest.
      setSelectedRunId((cur) =>
        cur && items.some((r) => r.run_id === cur) ? cur : (items[0]?.run_id ?? null),
      );
    } catch {
      /* non-fatal for the editor */
    }
  }, [getToken, pipelineId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const res = await getEditorState(token, pipelineId);
        if (cancelled) return;
        setEditable(res.editable);
        setLifecycle(res.lifecycle_state);
        setNextStates(res.allowed_next_states ?? []);
        setSelectedVersion(res.version_no);
        const loaded = res.graph as unknown as PipelineGraph;
        const { nodes: rfNodes, edges: rfEdges } = toReactFlow(loaded);
        const declared = loaded.parameters ?? [];
        setNodes(rfNodes);
        setEdges(rfEdges);
        setParameters(declared);
        lastSavedSigRef.current = sigOf(rfNodes, rfEdges, declared);
        setDirty(false);
        loadedRef.current = true;
        void refreshVersions();
        void refreshSchedule();
        void refreshBlobWatch();
        void refreshRuns();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    getToken,
    pipelineId,
    setNodes,
    setEdges,
    refreshVersions,
    refreshSchedule,
    refreshBlobWatch,
    refreshRuns,
  ]);

  // Refresh run history when the Execution tab opens.
  useEffect(() => {
    if (activeTab === "execution") void refreshRuns();
  }, [activeTab, refreshRuns]);

  // The pipeline's name, for the header.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const p = await getPipeline(token, pipelineId);
        if (!cancelled) {
          setPipelineName(p.name);
          setPipelineDescription(p.description ?? null);
          setOwnerUserId(p.created_by_user_id ?? null);
          setSolutionPage(p.solution_page ?? null);
        }
      } catch {
        /* non-fatal — the header falls back to the id */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, pipelineId]);

  // Track whether the canvas diverges from the loaded/saved baseline. NO
  // autosave — the graph is persisted only by Save/Run (which create a
  // version). The signature check ignores run-status annotations on nodes.
  useEffect(() => {
    if (!loadedRef.current) return;
    setDirty(sigOf(nodes, edges, parameters) !== lastSavedSigRef.current);
  }, [nodes, edges, parameters]);

  // Guard against losing unsaved edits by closing/reloading the tab or leaving
  // the URL. Unsaved work is not persisted anywhere, so warn before it's gone.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const isValidConnection = useCallback(
    (c: Connection | Edge) => {
      if (!c.source || !c.target || c.source === c.target) return false;
      return !wouldCreateCycle(edges, c.source, c.target);
    },
    [edges],
  );

  const onConnect = useCallback(
    (c: Connection) => {
      if (!isValidConnection(c)) return;
      setEdges((eds) => {
        // One wire per input port: drop any edge already on this target handle.
        const cleared = eds.filter(
          (e) =>
            !(e.target === c.target && (e.targetHandle ?? "in-0") === (c.targetHandle ?? "in-0")),
        );
        return addEdge(c, cleared);
      });
    },
    [isValidConnection, setEdges],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const kind = e.dataTransfer.getData("application/daquma-node");
      const def = NODE_BY_KIND.get(kind);
      if (!def) return;
      if (
        def.category === "trigger" &&
        nodes.some((n) => NODE_BY_KIND.get(String(n.data?.kind))?.category === "trigger")
      ) {
        toastApiError("Only one trigger", new Error("A pipeline can have only one trigger."));
        return;
      }
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const config = { ...def.defaultConfig };
      const node: Node = {
        id: newNodeId(),
        type: "pipelineNode",
        position,
        data: { kind, config },
      };
      setNodes((nds) => [...nds, isNoteKind(kind) ? asNoteNode(node, config) : node]);
    },
    [nodes, screenToFlowPosition, setNodes],
  );

  // Ctrl/Cmd-drag duplicates the dragged node(s). React Flow moves the node you
  // grabbed, so to keep the on-screen *original untouched* (position + all its
  // wires) we leave a stationary clone at the start position that INHERITS the
  // original's connections, and let the grabbed node carry on under the cursor
  // as a fresh, disconnected copy. Net effect the user sees: the connected node
  // stays put, a new unconnected copy follows the cursor. (The fresh id lands on
  // the stationary clone since a dragged node can't change id mid-drag; ids are
  // opaque, so this is invisible.) Edges *between* nodes in the dragged set are
  // duplicated onto the stationary copies; edges to outside nodes are handed to
  // the stationary copies (the moving copy is left disconnected). Config is
  // deep-copied so editing one never mutates the other. Nothing persists until
  // Save/Run (versions-only model).
  const onNodeDragStart = useCallback(
    (event: MouseEvent | TouchEvent, node: Node, dragged: Node[]) => {
      if (!editable || duplicatingRef.current) return;
      if (!(event.ctrlKey || event.metaKey)) return;
      duplicatingRef.current = true;
      const set = dragged.length ? dragged : [node];
      const ids = new Set(set.map((n) => n.id));
      const idMap = new Map<string, string>();
      for (const id of ids) idMap.set(id, newNodeId());
      const clones: Node[] = nodes
        .filter((n) => ids.has(n.id))
        .map((n) => ({
          ...n,
          id: idMap.get(n.id)!,
          position: { ...n.position },
          selected: false,
          dragging: false,
          data: {
            kind: n.data?.kind,
            config: JSON.parse(JSON.stringify(n.data?.config ?? {})) as Record<string, unknown>,
            disabled: Boolean(n.data?.disabled),
          },
        }));
      const edgeId = (
        s: string,
        sh: string | null | undefined,
        t: string,
        th: string | null | undefined,
      ) => `${s}:${sh ?? "out"}->${t}:${th ?? "in-0"}`;
      setNodes((nds) => [...nds, ...clones]);
      setEdges((eds) => {
        const out: Edge[] = [];
        for (const e of eds) {
          const sIn = ids.has(e.source);
          const tIn = ids.has(e.target);
          if (sIn && tIn) {
            // Internal to the set: the moving copy keeps it; the stationary
            // copies get a duplicate.
            out.push(e);
            const s = idMap.get(e.source)!;
            const t = idMap.get(e.target)!;
            out.push({
              ...e,
              id: edgeId(s, e.sourceHandle, t, e.targetHandle),
              source: s,
              target: t,
              selected: false,
            });
          } else if (sIn) {
            // Edge out of the set → hand it to the stationary copy so the
            // original stays connected; the moving copy loses it.
            const s = idMap.get(e.source)!;
            out.push({ ...e, id: edgeId(s, e.sourceHandle, e.target, e.targetHandle), source: s });
          } else if (tIn) {
            const t = idMap.get(e.target)!;
            out.push({ ...e, id: edgeId(e.source, e.sourceHandle, t, e.targetHandle), target: t });
          } else {
            out.push(e);
          }
        }
        return out;
      });
    },
    [editable, nodes, setNodes, setEdges],
  );

  const onNodeDragStop = useCallback(() => {
    duplicatingRef.current = false;
  }, []);

  const updateConfig = useCallback(
    (key: string, value: unknown) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === selectedId
            ? {
                ...n,
                data: {
                  ...n.data,
                  config: { ...((n.data?.config ?? {}) as Record<string, unknown>), [key]: value },
                },
              }
            : n,
        ),
      );
      if (key === "inputs" || key === "supporting") {
        // Reducing a Display's input count, or Metadata enrichment's supporting
        // inputs (in-0 + one port each), drops wires to the removed ports.
        const count =
          key === "supporting"
            ? 1 + (Array.isArray(value) ? value.length : 0)
            : Math.max(1, Number(value) || 1);
        setEdges((eds) =>
          eds.filter((e) => {
            if (e.target !== selectedId) return true;
            const idx = Number(String(e.targetHandle ?? "in-0").replace("in-", ""));
            return idx < count;
          }),
        );
      }
    },
    [selectedId, setNodes, setEdges],
  );

  const setDisabled = useCallback(
    (val: boolean) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === selectedId ? { ...n, data: { ...n.data, disabled: val } } : n)),
      );
    },
    [selectedId, setNodes],
  );

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedId && e.target !== selectedId));
    setSelectedId(null);
  }, [selectedId, setNodes, setEdges]);

  const applyRun = useCallback(
    (res: RunOut) => {
      const statusById = new Map(res.nodes.map((n) => [n.id, n.status]));
      const displaysByNode = new Map<string, DisplayResult[]>();
      for (const d of res.displays) {
        const arr = displaysByNode.get(d.node_id) ?? [];
        arr.push({ port: d.port, data: d.data });
        displaysByNode.set(d.node_id, arr);
      }
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          data: { ...n.data, runStatus: statusById.get(n.id), display: displaysByNode.get(n.id) },
        })),
      );
    },
    [setNodes],
  );

  // Paint the canvas from a stored run: per-node status and captured Display
  // output. Used for a scheduled run that fired while the tab was open, and for
  // a run that was already in flight when the page was opened — the operator
  // who comes back to the tab sees what the operator who stayed sees.
  const applyRunSummary = useCallback(
    (run: RunSummaryOut) => {
      const statusById = new Map((run.nodes ?? []).map((n) => [n.id, n.status]));
      const displaysByNode = new Map<string, DisplayResult[]>();
      for (const dsp of run.displays) {
        const arr = displaysByNode.get(dsp.node_id) ?? [];
        arr.push({ port: dsp.port, data: dsp.data });
        displaysByNode.set(dsp.node_id, arr);
      }
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          data: {
            ...n.data,
            // The run's own node list is the whole truth: a node it does not
            // list has no status in *this* run. Falling back to the previous
            // value left a node "running" forever once a later scheduled run
            // never reached it.
            runStatus: run.nodes ? statusById.get(n.id) : n.data?.runStatus,
            display: displaysByNode.get(n.id) ?? n.data?.display,
          },
        })),
      );
    },
    [setNodes],
  );

  // While an automated trigger is armed (a running schedule OR a blob watch),
  // poll for its state (countdown / watching) and the latest run, so the canvas
  // Display live-updates when it fires. Keyed only on the enabled flags so it
  // doesn't churn each poll.
  const scheduleEnabled = Boolean(schedule?.enabled);
  const blobEnabled = Boolean(blobWatch?.enabled);
  const armed = scheduleEnabled || blobEnabled;
  useEffect(() => {
    if (!armed) return;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const token = await getToken();
        const [sched, watch, runsRes] = await Promise.all([
          getSchedule(token, pipelineId),
          getBlobWatch(token, pipelineId),
          listRuns(token, pipelineId),
        ]);
        if (cancelled) return;
        setSchedule(sched);
        setBlobWatch(watch);
        setRuns(runsRes.items); // keep the Execution tab live
        // Every tick, not only when a new run appears: painting a run once,
        // while it was still running, froze its nodes in that state.
        const latest = runsRes.items[0];
        if (latest) {
          lastRunIdRef.current = latest.run_id;
          applyRunSummary(latest);
        }
      } catch {
        /* transient — try again next tick */
      }
    }, 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [armed, getToken, pipelineId, applyRunSummary]);

  // A run this tab did not start — or started before a reload. The newest run
  // sits at index 0; while it says `running` the server is still working on it.
  // A run is "in flight" from the moment it is queued: a worker claims it a
  // moment later, and between those two moments the pipeline is busy — showing
  // it idle would invite a second Run for work already asked for.
  const latestRun = runs[0];
  const liveRunId = latestRun && IN_FLIGHT.has(latestRun.status) ? latestRun.run_id : null;

  // Poll from the moment Run is pressed — `running` — and not only once a
  // refresh has happened to notice a running row — `liveRunId`.
  //
  // That distinction was the whole bug behind "the log is not live": the Run
  // request blocks for as long as the run takes, and nothing else refreshed
  // the run list while it did, so the first poll landed *after* the run had
  // finished and every line arrived at once. `running` is true the instant the
  // button is clicked, which is when the watching has to start.
  const watching = Boolean(liveRunId) || running;
  useEffect(() => {
    if (!watching) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const token = await getToken();
        const items = (await listRuns(token, pipelineId)).items;
        if (cancelled) return;
        setRuns(items);
        // The newest run is the one being watched: this tab just started it, or
        // it was already in flight when the page opened.
        const run = items[0];
        if (!run) return;
        // Every tick, not only at the end: the engine writes the per-node log
        // and the node statuses as it goes.
        applyRunSummary(run);
        if (!IN_FLIGHT.has(run.status)) {
          lastRunIdRef.current = run.run_id;
          // The run ended while we were watching it. The Run button returned
          // long ago (it only queued the run), so this is where its outcome is
          // reported — once per run, whoever started it.
          if (toldAboutRef.current !== run.run_id) {
            toldAboutRef.current = run.run_id;
            if (run.status === "succeeded")
              toastSuccess("Run complete", "Pipeline ran successfully.");
            else if (run.status === "failed")
              toastApiError(
                "Run failed",
                new Error(run.error ?? "A node failed — see node status."),
              );
          }
        }
      } catch {
        /* transient — try again next tick */
      }
    };
    // Immediately, then on a cadence: waiting a full interval before the first
    // line is the difference between "live" and "eventually".
    void tick();
    const timer = setInterval(() => void tick(), 1200);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [watching, getToken, pipelineId, applyRunSummary]);

  // The log of the run in flight, or — once it ends — of the run just shown.
  // Reading from `runs` rather than keeping a copy means the poll feeds it for
  // free, and reopening the page shows the last run's log without a refetch.
  const shownRun = runs.find((r) => r.run_id === (liveRunId ?? selectedRunId)) ?? runs[0];
  const logLines = shownRun?.log ?? [];

  // Busy means "the server is working on this pipeline", whoever asked it to —
  // this tab's Run button, another tab, or a schedule.
  const busy = running || liveRunId !== null;

  const runNow = useCallback(
    async (values?: Record<string, string>) => {
      setAskingParameters(false);
      setRunning(true);
      // Clear the last run's colours: leaving them up while the new one works
      // would show a graph of stale greens with one node pulsing inside it.
      setNodes((nds) =>
        nds.map((n) => ({ ...n, data: { ...n.data, runStatus: undefined, display: undefined } })),
      );
      try {
        const token = await getToken();
        // Run = save-then-run: the backend snapshots the submitted canvas as a
        // version and executes exactly what's on screen.
        const graph = fromReactFlow(nodes, edges, parameters) as unknown as Record<string, unknown>;
        const res = await runPipeline(token, pipelineId, graph, values);
        lastSavedSigRef.current = sigOf(nodes, edges, parameters);
        setDirty(false);
        setSelectedVersion(res.version_no);
        applyRun(res);
        lastRunIdRef.current = res.run_id;
        setSelectedRunId(res.run_id); // surface this run on the Execution tab
        // Run also arms the schedule / blob watch (if any) — pick up new state + history.
        void refreshVersions();
        void refreshSchedule();
        void refreshBlobWatch();
        void refreshRuns();
        // The run is queued, not finished: a worker picks it up. The watcher
        // above follows it and reports the outcome when there is one, which is
        // also how a run someone else started gets reported.
        toastSuccess("Run queued", `v${res.version_no} is queued — watch it on the canvas.`);
      } catch (err) {
        toastApiError("Could not run", err);
      } finally {
        setRunning(false);
      }
    },
    [
      getToken,
      pipelineId,
      nodes,
      edges,
      parameters,
      applyRun,
      refreshVersions,
      refreshSchedule,
      refreshBlobWatch,
      refreshRuns,
    ],
  );

  /** Run asks for values first when the pipeline declares any, and is one click
   *  when it does not. */
  const onRun = useCallback(() => {
    if (parameters.length > 0) {
      setAskingParameters(true);
      return;
    }
    void runNow();
  }, [parameters, runNow]);

  // Stop the run that is in flight. Distinct from disarming a trigger below:
  // one interrupts work already happening, the other stops future work being
  // scheduled, and conflating them behind one button would make "Stop" mean
  // whichever the operator was not thinking of.
  const onStopRun = useCallback(async () => {
    if (!liveRunId) return;
    setStoppingRun(true);
    try {
      const token = await getToken();
      await stopRun(token, pipelineId, liveRunId);
      toastSuccess("Stopping", "The run stops at its next checkpoint.");
      void refreshRuns();
    } catch (err) {
      toastApiError("Could not stop the run", err);
    } finally {
      setStoppingRun(false);
    }
  }, [getToken, pipelineId, liveRunId, refreshRuns]);

  const onStop = useCallback(async () => {
    setStopping(true);
    try {
      const token = await getToken();
      setSchedule(await stopPipeline(token, pipelineId));
      void refreshBlobWatch();
      toastSuccess("Stopped", "The trigger is disarmed.");
    } catch (err) {
      toastApiError("Could not stop", err);
    } finally {
      setStopping(false);
    }
  }, [getToken, pipelineId, refreshBlobWatch]);

  // The toolbar's one Stop: disarm the trigger (no further scheduled runs)
  // and stop the run in flight, whichever of the two is happening.
  const onStopAll = useCallback(async () => {
    if (armed) await onStop();
    if (liveRunId) await onStopRun();
  }, [armed, liveRunId, onStop, onStopRun]);

  // Save: snapshot the current canvas as a new immutable, numbered version.
  // This is the ONLY way a pipeline is persisted — there is no draft. Deduped
  // server-side, so saving an unchanged graph is a no-op.
  // Promotion into validation/production is administrator-only server-side
  // (ADR-0017). The menu hides those entries for everyone else, so the UI never
  // offers an action the backend would refuse.
  const PROMOTION_STATES = ["validation", "production"];

  // Only legal transitions appear, and promotions only for administrators —
  // an illegal or unauthorised option is never rendered rather than rendered
  // and rejected.
  const visibleNextStates = nextStates.filter(
    (state) => isAdministrator || !PROMOTION_STATES.includes(state),
  );

  const onTransition = async (to: string) => {
    const reason = await promptForReason(
      `Move this pipeline to ${to}?`,
      "Reason (recorded in the audit trail)",
    );
    if (reason === null) return;
    setTransitioning(true);
    try {
      const token = await getToken();
      const updated = await transitionPipelineLifecycle(token, pipelineId, {
        to_state: to,
        reason,
      });
      setLifecycle(updated.lifecycle_state);
      setNextStates(updated.allowed_next_states ?? []);
      setEditable(updated.is_editable ?? false);
      toastSuccess("Lifecycle updated", `This pipeline is now ${updated.lifecycle_state}.`);
    } catch (err) {
      toastApiError("Could not change the lifecycle state", err);
    } finally {
      setTransitioning(false);
    }
  };

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      const token = await getToken();
      const graph = fromReactFlow(nodes, edges, parameters) as unknown as Record<string, unknown>;
      const { version_no } = await saveVersion(token, pipelineId, graph);
      lastSavedSigRef.current = sigOf(nodes, edges, parameters);
      setDirty(false);
      setSelectedVersion(version_no);
      void refreshVersions();
      void refreshSchedule();
      void refreshBlobWatch();
      toastSuccess("Saved", `Saved as version ${version_no}.`);
      // Saved either way — a draft may be mid-edit — but say now what the run
      // will refuse (§9.6b: AI output passes a person before it is written).
      const violations = aiWriteViolations(graph as unknown as PipelineGraph);
      if (violations.length > 0) {
        toastWarning("This pipeline cannot run yet", violations.join(" "));
      }
    } catch (err) {
      toastApiError("Could not save", err);
    } finally {
      setSaving(false);
    }
  }, [
    getToken,
    pipelineId,
    nodes,
    edges,
    parameters,
    refreshVersions,
    refreshSchedule,
    refreshBlobWatch,
  ]);

  // Load a saved version into the canvas as the new editing base. Editing it
  // and Save/Run creates the NEXT version — the loaded one stays immutable.
  const onLoadVersion = useCallback(
    async (versionNo: number) => {
      try {
        const token = await getToken();
        const res = await getVersion(token, pipelineId, versionNo);
        const loaded = res.graph as unknown as PipelineGraph;
        const { nodes: rfNodes, edges: rfEdges } = toReactFlow(loaded);
        const declared = loaded.parameters ?? [];
        setNodes(rfNodes);
        setEdges(rfEdges);
        setParameters(declared);
        setSelectedId(null);
        lastSavedSigRef.current = sigOf(rfNodes, rfEdges, declared);
        setDirty(false);
        setSelectedVersion(versionNo);
        toastSuccess(
          "Loaded version " + versionNo,
          "Edit and Save to create a new version, or Run.",
        );
      } catch (err) {
        toastApiError("Could not load version", err);
      }
    },
    [getToken, pipelineId, setNodes, setEdges],
  );

  // Export the current on-canvas graph as a clean PipelineGraph JSON download.
  // Runtime/display annotations never reach here — fromReactFlow keeps only
  // type/config/pos + edges, so the file round-trips cleanly.
  const onExport = useCallback(() => {
    const graph = fromReactFlow(nodes, edges);
    const slug =
      (pipelineName || "pipeline").replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "") || "pipeline";
    const vtag = selectedVersion != null ? `v${selectedVersion}` : "unsaved";
    const blob = new Blob([JSON.stringify(graph, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}-${vtag}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [nodes, edges, pipelineName, selectedVersion]);

  // Import: parse + shape-validate the file, then stage it for a confirm step —
  // replacing the whole canvas is destructive, so never clobber silently.
  const onImportFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be picked again later
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as unknown;
        const g = parsed as Partial<PipelineGraph>;
        if (
          !parsed ||
          typeof parsed !== "object" ||
          Array.isArray(parsed) ||
          !Array.isArray(g.nodes) ||
          !Array.isArray(g.edges)
        ) {
          throw new Error("File must be a pipeline graph with `nodes` and `edges` arrays.");
        }
        setPendingImport({ graph: parsed as PipelineGraph, filename: file.name });
      } catch (err) {
        toastApiError("Invalid pipeline file", err instanceof Error ? err : new Error(String(err)));
      }
    };
    reader.onerror = () => toastApiError("Could not read file", new Error("File read failed."));
    reader.readAsText(file);
  }, []);

  // Confirmed import: fully replace the canvas with the file, then save it as a
  // new version (import = replace → save; the new version becomes the latest).
  const onConfirmImport = useCallback(async () => {
    if (!pendingImport) return;
    const graph = pendingImport.graph;
    try {
      const { nodes: rfNodes, edges: rfEdges } = toReactFlow(graph);
      setNodes(rfNodes);
      setEdges(rfEdges);
      setSelectedId(null);
      setSourceColumns({});
      const token = await getToken();
      const { version_no } = await saveVersion(
        token,
        pipelineId,
        graph as unknown as Record<string, unknown>,
      );
      lastSavedSigRef.current = sigOf(rfNodes, rfEdges);
      setDirty(false);
      setSelectedVersion(version_no);
      void refreshVersions();
      void refreshSchedule();
      void refreshBlobWatch();
      toastSuccess("Imported", `Replaced the pipeline and saved as version ${version_no}.`);
    } catch (err) {
      toastApiError("Could not import", err);
    } finally {
      setPendingImport(null);
    }
  }, [
    pendingImport,
    getToken,
    pipelineId,
    setNodes,
    setEdges,
    refreshVersions,
    refreshSchedule,
    refreshBlobWatch,
  ]);

  // A pipeline must start with a trigger. Compute which nodes are reachable
  // from the trigger(s); anything else is an orphan (flagged, and it won't run).
  const triggerIds = useMemo(
    () =>
      nodes
        .filter((n) => NODE_BY_KIND.get(String(n.data?.kind))?.category === "trigger")
        .map((n) => n.id),
    [nodes],
  );
  const hasTrigger = triggerIds.length > 0;
  const hasSchedule = useMemo(
    () => nodes.some((n) => String(n.data?.kind) === "trigger.schedule"),
    [nodes],
  );
  const hasBlobTrigger = useMemo(
    () => nodes.some((n) => String(n.data?.kind) === "trigger.blob"),
    [nodes],
  );
  const orphanIds = useMemo(() => {
    if (!hasTrigger) return new Set<string>();
    const adj = new Map<string, string[]>();
    for (const e of edges) {
      const out = adj.get(e.source) ?? [];
      out.push(e.target);
      adj.set(e.source, out);
    }
    const reach = new Set<string>();
    const stack = [...triggerIds];
    while (stack.length) {
      const n = stack.pop()!;
      if (reach.has(n)) continue;
      reach.add(n);
      for (const m of adj.get(n) ?? []) stack.push(m);
    }
    // Notes are annotations: never reachable, never "not connected".
    return new Set(
      nodes
        .filter((n) => !reach.has(n.id) && !isNoteKind(String(n.data?.kind ?? "")))
        .map((n) => n.id),
    );
  }, [nodes, edges, triggerIds, hasTrigger]);
  // In-place edits from a note on the canvas.
  const onNoteChange = useCallback(
    (id: string, patch: NotePatch) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  config: { ...((n.data?.config ?? {}) as Record<string, unknown>), ...patch },
                },
              }
            : n,
        ),
      );
    },
    [setNodes],
  );
  // Inject `orphan` + live `schedule` at render time (derived — not stored, so
  // they never autosave). Only the Schedule trigger gets the schedule state.
  // The gate the newest run is waiting at, shown on the Approval node's own box
  // (a red | green toggle) rather than in a separate dialog.
  // The newest *waiting* run, not just the newest run: on a schedule a fresh run
  // starts every tick, and taking runs[0] hid the gate of the one still waiting.
  const waitingRunId = runs.find((r) => r.status === "waiting")?.run_id ?? null;
  const [runApprovals, setRunApprovals] = useState<ApprovalOut[]>([]);
  const loadRunApprovals = useCallback(async () => {
    if (!waitingRunId) {
      setRunApprovals([]);
      return;
    }
    try {
      const token = await getToken();
      setRunApprovals((await listRunApprovals(token, pipelineId, waitingRunId)).items);
    } catch {
      /* transient — the next tick tries again */
    }
  }, [getToken, pipelineId, waitingRunId]);
  useEffect(() => {
    void loadRunApprovals();
    if (!waitingRunId) return;
    const timer = setInterval(() => void loadRunApprovals(), 3000);
    return () => clearInterval(timer);
  }, [loadRunApprovals, waitingRunId]);
  const approvalByNode = useMemo(
    () => new Map(runApprovals.filter((a) => a.status === "pending").map((a) => [a.node_id, a])),
    [runApprovals],
  );

  const flowNodes = useMemo(
    () =>
      nodes.map((n) =>
        isNoteKind(String(n.data?.kind ?? ""))
          ? { ...n, data: { ...n.data, editable, onNoteChange } }
          : {
              ...n,
              data: {
                ...n.data,
                orphan: orphanIds.has(n.id),
                schedule: String(n.data?.kind) === "trigger.schedule" ? schedule : undefined,
                blobWatch: String(n.data?.kind) === "trigger.blob" ? blobWatch : undefined,
                // While a run is in flight, non-trigger nodes show a working sweep
                // (we can't stream per-node status from a synchronous run). `busy`,
                // not `running`: a run this tab did not start is still a run.
                running: busy,
                approval: approvalByNode.get(n.id),
                onApprovalDecided: loadRunApprovals,
              },
            },
      ),
    [
      nodes,
      orphanIds,
      schedule,
      blobWatch,
      busy,
      editable,
      onNoteChange,
      approvalByNode,
      loadRunApprovals,
    ],
  );

  // Arrowheads are drawn, not stored: which way an edge points is already in
  // the graph, and the marker is only how the canvas says so.
  const flowEdges = useMemo(() => withArrows(edges), [edges]);

  // Current-graph node labels, for the run log (a historical run may reference a
  // node that no longer exists → the table falls back to the short id).
  const nodeLabelById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const n of nodes) {
      const kind = String(n.data?.kind ?? "");
      m[n.id] = NODE_BY_KIND.get(kind)?.label ?? kind;
    }
    return m;
  }, [nodes]);

  if (loading) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
      </Group>
    );
  }
  if (error) {
    return (
      <Alert variant="light" color="red" title="Could not load pipeline">
        {error}
      </Alert>
    );
  }

  const selected = nodes.find((n) => n.id === selectedId) ?? null;

  return (
    // The canvas fills the viewport, minus exactly what the shell puts above
    // and below it. It used to subtract a hardcoded 130px, most of which was
    // the top bar that no longer exists — a magic number that silently became
    // wrong the moment the chrome around it changed. These are Mantine's own
    // AppShell tokens (the same ones that set Main's padding), so bringing a
    // header or footer back would adjust this without anyone remembering to.
    <Stack
      gap={6}
      style={{
        height:
          "calc(100vh - var(--app-shell-header-offset, 0rem)" +
          " - var(--app-shell-footer-offset, 0rem)" +
          " - 2 * var(--app-shell-padding, 16px))",
      }}
    >
      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        style={{ display: "none" }}
        onChange={onImportFile}
      />
      <Modal
        opened={pendingImport !== null}
        onClose={() => setPendingImport(null)}
        title="Replace this pipeline?"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            Importing <b>{pendingImport?.filename}</b> replaces the <b>entire pipeline</b> on the
            canvas and saves it as a new version. The current canvas contents are discarded.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPendingImport(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={() => void onConfirmImport()}>
              Replace &amp; save
            </Button>
          </Group>
        </Stack>
      </Modal>
      {/* One compact row, everything the operator reaches for on the left and
          the status they only read on the right. Small controls on purpose:
          every pixel here is a pixel the canvas does not get, and the canvas
          is the thing being worked on. */}
      <Group gap="xs" wrap="nowrap">
        <ActionIcon
          component={RouterLink}
          to={`/projects/${projectId}`}
          variant="subtle"
          size="sm"
          aria-label="Back to project"
        >
          <IconArrowLeft size={16} />
        </ActionIcon>
        <Title order={5} style={{ whiteSpace: "nowrap" }}>
          {pipelineName || "Pipeline canvas"}
        </Title>
        {editable && (
          <Tooltip label="Rename">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Rename pipeline"
              onClick={() => setRenaming(true)}
            >
              <IconPencil size={14} />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label="Share">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Share pipeline"
            onClick={() => setSharing(true)}
          >
            <IconShare size={14} />
          </ActionIcon>
        </Tooltip>
        <Badge size="sm" variant="light" tt="capitalize">
          {lifecycle}
        </Badge>
        {visibleNextStates.length > 0 && (
          <Menu shadow="md" position="bottom-start">
            <Menu.Target>
              <Button
                variant="subtle"
                size="compact-xs"
                loading={transitioning}
                rightSection={<IconChevronDown size={13} />}
              >
                State
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Move to</Menu.Label>
              {visibleNextStates.map((state) => (
                <Menu.Item key={state} tt="capitalize" onClick={() => void onTransition(state)}>
                  {state}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}

        <Divider orientation="vertical" my={4} />

        {/* One slot of a fixed width: "Stop run" is a wider word than "Run",
            and without this the buttons beside it jump sideways the moment a
            run starts — which is when they are most likely to be clicked. */}
        <Box w={96} style={{ flex: "0 0 auto" }}>
          {busy || armed ? (
            // An armed schedule / blob watch is "running" too: Run started it,
            // so Stop is what ends it. Replaces Run rather than sitting beside it: while a run is in
            // flight the only useful verb is "stop", and a disabled Run next to
            // an enabled Stop is two controls saying one thing.
            <Button
              fullWidth
              size="xs"
              color="red"
              leftSection={<IconPlayerStop size={14} />}
              onClick={() => void onStopAll()}
              loading={stoppingRun || stopping}
              disabled={!liveRunId && !armed}
            >
              Stop
            </Button>
          ) : (
            <Button
              fullWidth
              size="xs"
              leftSection={<IconPlayerPlay size={14} />}
              onClick={() => void onRun()}
              disabled={!hasTrigger || saving}
            >
              Run
            </Button>
          )}
        </Box>

        <Button
          variant="default"
          size="xs"
          leftSection={<IconDeviceFloppy size={14} />}
          onClick={() => void onSave()}
          loading={saving}
          // Not while a run is in flight: Run saves a version first, so the two
          // would race for the same next version number.
          disabled={!editable || !dirty || busy}
        >
          Save
        </Button>

        <Menu shadow="md" width={300} position="bottom-start">
          <Menu.Target>
            <Button
              variant="default"
              size="xs"
              leftSection={<IconHistory size={14} />}
              rightSection={<IconChevronDown size={13} />}
            >
              {selectedVersion !== null ? `v${selectedVersion}` : "Versions"}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Saved versions (immutable snapshots)</Menu.Label>
            {versions.length === 0 ? (
              <Menu.Item disabled>None yet — Save to create v1</Menu.Item>
            ) : (
              versions.map((v) => (
                <Menu.Item
                  key={v.version_no}
                  onClick={() => void onLoadVersion(v.version_no)}
                  rightSection={
                    selectedVersion === v.version_no ? (
                      <Text size="xs" c="dimmed">
                        {dirty ? "editing*" : "selected"}
                      </Text>
                    ) : undefined
                  }
                >
                  Version {v.version_no} — {formatUtc(v.created_at_utc)}
                </Menu.Item>
              ))
            )}
          </Menu.Dropdown>
        </Menu>

        {/* Export, Import and disarming a trigger are occasional. Behind one
            icon they cost nothing until wanted, instead of three buttons' width
            on every screen. */}
        <Menu shadow="md" position="bottom-start">
          <Menu.Target>
            <ActionIcon variant="default" size="md" aria-label="More actions">
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {isDeveloperPlus && (
              <Menu.Item
                leftSection={<IconLayoutDashboard size={14} />}
                onClick={() => setPublishing(true)}
              >
                Show results in…
              </Menu.Item>
            )}
            <Menu.Item leftSection={<IconDownload size={14} />} onClick={onExport}>
              Export graph
            </Menu.Item>
            <Menu.Item
              leftSection={<IconUpload size={14} />}
              disabled={!editable}
              onClick={() => importInputRef.current?.click()}
            >
              Import graph
            </Menu.Item>
            {(hasSchedule || hasBlobTrigger) && (
              <>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconPlayerStop size={14} />}
                  disabled={!armed}
                  onClick={() => void onStop()}
                >
                  {stopping ? "Disarming…" : "Disarm trigger"}
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>

        {!hasTrigger && (
          <Text size="xs" c="dimmed">
            Add a trigger to run
          </Text>
        )}

        {/* Status is read, not clicked, so it sits out of the way on the right
            — where its width changing on every save moves nothing. */}
        <Box style={{ marginLeft: "auto" }}>
          {!editable ? (
            <Badge size="sm" color="gray" variant="outline">
              read-only in {lifecycle}
            </Badge>
          ) : dirty ? (
            <Badge size="sm" color="yellow" variant="light">
              Unsaved changes
            </Badge>
          ) : (
            <Text size="xs" c="dimmed">
              {selectedVersion !== null ? `Saved · v${selectedVersion}` : "Nothing saved yet"}
            </Text>
          )}
        </Box>
      </Group>

      <ShareDialog
        opened={sharing}
        onClose={() => setSharing(false)}
        resourceType="pipeline"
        resourceId={pipelineId}
        resourceName={pipelineName || "this pipeline"}
        canShare={Boolean(me && ownerUserId && ownerUserId === me.user_id)}
      />

      <PublishToSolutionModal
        opened={publishing}
        onClose={() => setPublishing(false)}
        current={solutionPage}
        lifecycle={lifecycle}
        onSubmit={async (page) => {
          const updated = await setPipelineSolutionPage(await getToken(), pipelineId, page);
          setSolutionPage(updated.solution_page ?? null);
        }}
      />

      <RenameModal
        opened={renaming}
        onClose={() => setRenaming(false)}
        what="pipeline"
        name={pipelineName}
        description={pipelineDescription}
        onSubmit={async (name, description) => {
          const updated = await renamePipeline(await getToken(), pipelineId, name, description);
          setPipelineName(updated.name);
          setPipelineDescription(updated.description ?? null);
        }}
      />

      <Tabs
        value={activeTab}
        onChange={(v) => setActiveTab(v ?? "workflow")}
        style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Tabs.List>
          <Tabs.Tab value="workflow" leftSection={<IconSitemap size={16} />}>
            Workflow
          </Tabs.Tab>
          <Tabs.Tab
            value="log"
            leftSection={<IconTerminal2 size={16} />}
            rightSection={
              liveRunId ? (
                <Badge size="xs" color="yellow" variant="filled" circle>
                  {" "}
                </Badge>
              ) : undefined
            }
          >
            Live log
          </Tabs.Tab>
          <Tabs.Tab value="parameters" leftSection={<IconVariable size={16} />}>
            Parameters{parameters.length > 0 ? ` (${parameters.length})` : ""}
          </Tabs.Tab>
          <Tabs.Tab value="execution" leftSection={<IconListDetails size={16} />}>
            Execution{runs.length > 0 ? ` (${runs.length})` : ""}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel
          value="workflow"
          pt="xs"
          style={{ flex: 1, minHeight: 0, display: activeTab === "workflow" ? "flex" : undefined }}
        >
          <Group
            ref={containerRef}
            align="stretch"
            gap={0}
            style={{ flex: 1, minHeight: 0 }}
            wrap="nowrap"
          >
            <Paper
              withBorder
              radius="md"
              style={{ width: paletteWidth, overflowY: "auto", flex: "0 0 auto" }}
            >
              <Accordion multiple defaultValue={[]} chevronPosition="right">
                {CATEGORY_ORDER.map((cat) => {
                  // Within a category, list nodes alphabetically by label.
                  const defs = NODE_TYPES.filter((n) => n.category === cat).sort((a, b) =>
                    a.label.localeCompare(b.label),
                  );
                  if (defs.length === 0) return null;
                  return (
                    <Accordion.Item key={cat} value={cat}>
                      <Accordion.Control
                        icon={
                          <Box
                            w={8}
                            h={8}
                            style={{
                              borderRadius: "50%",
                              background: `var(--mantine-color-${CATEGORY_COLOR[cat]}-5)`,
                            }}
                          />
                        }
                      >
                        {CATEGORY_LABEL[cat]}
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="xs">
                          {defs.map((def) => (
                            <Paper
                              key={def.kind}
                              withBorder
                              radius="sm"
                              p="xs"
                              draggable={editable}
                              onDragStart={(e) =>
                                e.dataTransfer.setData("application/daquma-node", def.kind)
                              }
                              style={{
                                cursor: editable ? "grab" : "not-allowed",
                                opacity: editable ? 1 : 0.5,
                                borderLeft: `3px solid var(--mantine-color-${CATEGORY_COLOR[cat]}-5)`,
                              }}
                            >
                              <Text size="sm" fw={500}>
                                {def.label}
                              </Text>
                            </Paper>
                          ))}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            </Paper>

            <ResizeHandle onDrag={(dx) => setPaletteWidth((w) => clampPalette(w + dx))} />

            <Box
              style={{
                flex: 1,
                minWidth: 0,
                border: "1px solid var(--mantine-color-default-border)",
                borderRadius: 8,
                position: "relative",
              }}
              onDrop={onDrop}
              onDragOver={onDragOver}
            >
              <ReactFlow
                nodes={flowNodes}
                edges={flowEdges}
                nodeTypes={nodeTypes}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                isValidConnection={isValidConnection}
                onNodeClick={(_, n) => setSelectedId(n.id)}
                onPaneClick={() => setSelectedId(null)}
                onNodeDragStart={onNodeDragStart}
                onNodeDragStop={onNodeDragStop}
                nodesDraggable={editable}
                nodesConnectable={editable}
                selectionOnDrag
                selectionMode={SelectionMode.Partial}
                multiSelectionKeyCode="Shift"
                panOnDrag={[1, 2]}
                panOnScroll
                deleteKeyCode={editable ? ["Delete", "Backspace"] : null}
                fitView
              >
                <Background />
                <Controls />
              </ReactFlow>
            </Box>

            <ResizeHandle onDrag={(dx) => setInspectorWidth((w) => clampInspector(w - dx))} />

            <Paper
              withBorder
              radius="md"
              p="sm"
              style={{ width: inspectorWidth, overflowY: "auto", flex: "0 0 auto" }}
            >
              <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb="xs">
                Inspector
              </Text>
              {selected && isNoteKind(String(selected.data?.kind ?? "")) ? (
                <NoteInspector
                  key={selected.id}
                  kind={String(selected.data?.kind ?? "")}
                  config={(selected.data?.config ?? {}) as Record<string, unknown>}
                  editable={editable}
                  onChange={updateConfig}
                  onDelete={deleteSelected}
                />
              ) : selected ? (
                <NodeInspector
                  node={selected}
                  editable={editable}
                  onChange={updateConfig}
                  onToggle={setDisabled}
                  onDelete={deleteSelected}
                  inputColumns={inputColsByNode[selected.id] ?? {}}
                  upstream={upstreamVeeva(nodes, edges, selected.id)}
                  onColumns={handleColumns}
                  projectId={projectId}
                  pipelineId={pipelineId}
                  lifecycle={lifecycle}
                  getGraph={() =>
                    fromReactFlow(nodes, edges, parameters) as unknown as Record<string, unknown>
                  }
                />
              ) : (
                <Text size="sm" c="dimmed">
                  Select a node to configure it.
                </Text>
              )}
            </Paper>
          </Group>
        </Tabs.Panel>

        <Tabs.Panel
          value="log"
          pt="sm"
          pb="xs"
          style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
        >
          <RunLog
            lines={logLines}
            labelOf={(id) => nodeLabelById[id] ?? id}
            live={Boolean(liveRunId)}
            startedAt={shownRun?.started_at_utc}
          />
        </Tabs.Panel>

        <Tabs.Panel value="parameters" pt="sm" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <ParametersPanel parameters={parameters} editable={editable} onChange={setParameters} />
        </Tabs.Panel>

        <Tabs.Panel value="execution" pt="sm" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <ExecutionPanel
            runs={runs}
            selectedRunId={selectedRunId}
            onSelect={setSelectedRunId}
            onRefresh={() => void refreshRuns()}
            pipelineId={pipelineId}
            nodeLabelById={nodeLabelById}
          />
        </Tabs.Panel>
        <RunParametersModal
          parameters={parameters}
          opened={askingParameters}
          onClose={() => setAskingParameters(false)}
          onRun={(values) => void runNow(values)}
        />
      </Tabs>
    </Stack>
  );
}

/** Config for every `transform.*` node. Column pickers take `columns` as
 *  strict picklists of the columns that actually arrive at this node (computed
 *  by forward propagation). `in-0` = the single/left input, `in-1` = the join's
 *  right input. Saved values are always kept in the option list so config
 *  survives even before a source has been Tested. */
function TransformConfig({
  kind,
  config,
  editable,
  onChange,
  inputColumns,
}: {
  kind: string;
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  inputColumns: Record<string, string[]>;
}) {
  const in0 = inputColumns["in-0"] ?? [];
  const in1 = inputColumns["in-1"] ?? [];
  // Option list that always includes the currently-saved value(s), so a strict
  // picklist never silently drops config when columns aren't loaded yet.
  const opts = (base: string[], ...current: string[]) =>
    Array.from(new Set([...base, ...current.filter(Boolean)]));
  const selectedCols = Array.isArray(config.columns) ? config.columns.map((c) => String(c)) : [];
  const columnsPicker = (label: string, description?: string) => (
    <MultiSelect
      label={label}
      description={description}
      data={opts(in0, ...selectedCols)}
      value={selectedCols}
      disabled={!editable}
      searchable
      clearable
      placeholder={in0.length ? "Pick columns" : "Test the source to load columns"}
      onChange={(v) => onChange("columns", v)}
    />
  );

  if (kind === "transform.convert_type") {
    return (
      <Stack gap="xs">
        {columnsPicker("Columns to convert")}
        <Select
          label="Convert to"
          allowDeselect={false}
          disabled={!editable}
          value={String(config.to ?? "number")}
          data={[
            { value: "number", label: "Number" },
            { value: "integer", label: "Integer" },
            { value: "boolean", label: "Boolean" },
            { value: "date", label: "Date (ISO)" },
            { value: "text", label: "Text" },
          ]}
          onChange={(v) => onChange("to", v ?? "number")}
        />
        <Select
          label="If a value can't convert"
          allowDeselect={false}
          disabled={!editable}
          value={String(config.onError ?? "null")}
          data={[
            { value: "null", label: "Set it empty" },
            { value: "keep", label: "Keep the original" },
          ]}
          onChange={(v) => onChange("onError", v ?? "null")}
        />
      </Stack>
    );
  }

  if (kind === "transform.select") {
    return (
      <Stack gap="xs">
        <Select
          label="Mode"
          allowDeselect={false}
          disabled={!editable}
          value={String(config.mode ?? "keep")}
          data={[
            { value: "keep", label: "Keep these columns" },
            { value: "drop", label: "Drop these columns" },
          ]}
          onChange={(v) => onChange("mode", v ?? "keep")}
        />
        {columnsPicker("Columns")}
      </Stack>
    );
  }

  if (kind === "transform.trim") {
    return (
      <Stack gap="xs">
        {columnsPicker("Columns", "Leave empty to clean all text columns")}
        <Switch
          label="Collapse internal whitespace"
          checked={Boolean(config.collapse)}
          disabled={!editable}
          onChange={(e) => onChange("collapse", e.currentTarget.checked)}
        />
        <Select
          label="Change case"
          allowDeselect={false}
          disabled={!editable}
          value={String(config.case ?? "none")}
          data={[
            { value: "none", label: "Leave as-is" },
            { value: "lower", label: "lower case" },
            { value: "upper", label: "UPPER CASE" },
            { value: "title", label: "Title Case" },
          ]}
          onChange={(v) => onChange("case", v ?? "none")}
        />
      </Stack>
    );
  }

  if (kind === "transform.fill_null") {
    return (
      <Stack gap="xs">
        {columnsPicker("Columns", "Leave empty to fill all columns")}
        <TextInput
          label="Fill missing with"
          value={String(config.value ?? "")}
          disabled={!editable}
          onChange={(e) => onChange("value", e.currentTarget.value)}
        />
        <Switch
          label="Treat blank / whitespace as missing"
          checked={config.treatBlank !== false}
          disabled={!editable}
          onChange={(e) => onChange("treatBlank", e.currentTarget.checked)}
        />
      </Stack>
    );
  }

  if (kind === "transform.filter") {
    const op = String(config.op ?? "not_empty");
    const needsValue = op !== "empty" && op !== "not_empty";
    return (
      <Stack gap="xs">
        <Select
          label="Column"
          data={opts(in0, String(config.column ?? ""))}
          value={String(config.column ?? "") || null}
          disabled={!editable}
          searchable
          placeholder={in0.length ? "Pick a column" : "Test the source to load columns"}
          onChange={(v) => onChange("column", v ?? "")}
        />
        <Select
          label="Condition"
          allowDeselect={false}
          disabled={!editable}
          value={op}
          data={[
            { value: "eq", label: "equals" },
            { value: "ne", label: "does not equal" },
            { value: "contains", label: "contains" },
            { value: "not_contains", label: "does not contain" },
            { value: "not_empty", label: "is not empty" },
            { value: "empty", label: "is empty" },
            { value: "gt", label: "> (number)" },
            { value: "lt", label: "< (number)" },
            { value: "gte", label: ">= (number)" },
            { value: "lte", label: "<= (number)" },
          ]}
          onChange={(v) => onChange("op", v ?? "not_empty")}
        />
        {needsValue && (
          <TextInput
            label="Value"
            value={String(config.value ?? "")}
            disabled={!editable}
            onChange={(e) => onChange("value", e.currentTarget.value)}
          />
        )}
      </Stack>
    );
  }

  if (kind === "transform.replace") {
    const match = String(config.match ?? "equals");
    const pairs = Array.isArray(config.pairs)
      ? (config.pairs as { from?: string; to?: string }[])
      : [];
    const setPair = (i: number, key: "from" | "to", val: string) =>
      onChange(
        "pairs",
        pairs.map((p, idx) => (idx === i ? { ...p, [key]: val } : p)),
      );
    return (
      <Stack gap="xs">
        <Select
          label="Column"
          data={opts(in0, String(config.column ?? ""))}
          value={String(config.column ?? "") || null}
          disabled={!editable}
          searchable
          placeholder={in0.length ? "Pick a column" : "Test the source to load columns"}
          onChange={(v) => onChange("column", v ?? "")}
        />
        <Select
          label="Operation"
          allowDeselect={false}
          disabled={!editable}
          value={match}
          data={[
            { value: "equals", label: "Cell equals → replace whole cell" },
            { value: "contains", label: "Cell contains → replace whole cell" },
            { value: "substring", label: "Replace matching text within the cell" },
            { value: "map", label: "Value map — many old → new at once" },
          ]}
          onChange={(v) => onChange("match", v ?? "equals")}
        />
        {match === "map" ? (
          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              A cell equal to a “from” becomes its “to”; anything else is left alone.
            </Text>
            {pairs.map((p, i) => (
              <Group key={i} gap={4} wrap="nowrap" align="flex-end">
                <TextInput
                  label={i === 0 ? "From" : undefined}
                  value={String(p.from ?? "")}
                  disabled={!editable}
                  placeholder="old value"
                  style={{ flex: 1 }}
                  onChange={(e) => setPair(i, "from", e.currentTarget.value)}
                />
                <Text pb={6}>→</Text>
                <TextInput
                  label={i === 0 ? "To" : undefined}
                  value={String(p.to ?? "")}
                  disabled={!editable}
                  placeholder="new value"
                  style={{ flex: 1 }}
                  onChange={(e) => setPair(i, "to", e.currentTarget.value)}
                />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  disabled={!editable}
                  aria-label="Remove pair"
                  onClick={() =>
                    onChange(
                      "pairs",
                      pairs.filter((_, idx) => idx !== i),
                    )
                  }
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
            <Button
              size="compact-xs"
              variant="light"
              disabled={!editable}
              onClick={() => onChange("pairs", [...pairs, { from: "", to: "" }])}
            >
              Add mapping
            </Button>
          </Stack>
        ) : (
          <>
            <TextInput
              label="Find"
              value={String(config.find ?? "")}
              disabled={!editable}
              onChange={(e) => onChange("find", e.currentTarget.value)}
            />
            <TextInput
              label={
                match === "substring"
                  ? "Replace the matched text with"
                  : "Replace the whole cell with"
              }
              value={String(config.replace ?? "")}
              disabled={!editable}
              onChange={(e) => onChange("replace", e.currentTarget.value)}
            />
          </>
        )}
      </Stack>
    );
  }

  if (kind === "transform.sql") {
    return <SqlConfig config={config} editable={editable} onChange={onChange} columns={in0} />;
  }

  if (kind === "transform.rename") {
    const renames = Array.isArray(config.renames)
      ? (config.renames as { from?: string; to?: string }[])
      : [];
    const setRow = (i: number, key: "from" | "to", val: string) =>
      onChange(
        "renames",
        renames.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)),
      );
    return (
      <Stack gap="xs">
        {renames.length === 0 && (
          <Text size="xs" c="dimmed">
            No renames yet — add one.
          </Text>
        )}
        {renames.map((r, i) => (
          <Group key={i} gap={4} wrap="nowrap" align="flex-end">
            <Select
              label={i === 0 ? "From" : undefined}
              data={opts(in0, String(r.from ?? ""))}
              value={String(r.from ?? "") || null}
              disabled={!editable}
              searchable
              placeholder="column"
              style={{ flex: 1 }}
              onChange={(v) => setRow(i, "from", v ?? "")}
            />
            <Text pb={6}>→</Text>
            <TextInput
              label={i === 0 ? "To" : undefined}
              value={String(r.to ?? "")}
              disabled={!editable}
              placeholder="new name"
              style={{ flex: 1 }}
              onChange={(e) => setRow(i, "to", e.currentTarget.value)}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              disabled={!editable}
              aria-label="Remove"
              onClick={() =>
                onChange(
                  "renames",
                  renames.filter((_, idx) => idx !== i),
                )
              }
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}
        <Button
          size="xs"
          variant="light"
          disabled={!editable}
          onClick={() => onChange("renames", [...renames, { from: "", to: "" }])}
        >
          Add rename
        </Button>
      </Stack>
    );
  }

  if (kind === "transform.join") {
    return (
      <Stack gap="xs">
        <Select
          label="Left key (top input)"
          data={opts(in0, String(config.leftKey ?? ""))}
          value={String(config.leftKey ?? "") || null}
          disabled={!editable}
          searchable
          placeholder="key column"
          onChange={(v) => onChange("leftKey", v ?? "")}
        />
        <Select
          label="Right key (bottom input)"
          data={opts(in1, String(config.rightKey ?? ""))}
          value={String(config.rightKey ?? "") || null}
          disabled={!editable}
          searchable
          placeholder="key column"
          onChange={(v) => onChange("rightKey", v ?? "")}
        />
        <Select
          label="Join type"
          allowDeselect={false}
          disabled={!editable}
          value={String(config.how ?? "inner")}
          data={[
            { value: "inner", label: "Inner (only matches)" },
            { value: "left", label: "Left (all of top)" },
            { value: "right", label: "Right (all of bottom)" },
            { value: "outer", label: "Outer (all rows)" },
          ]}
          onChange={(v) => onChange("how", v ?? "inner")}
        />
        <TextInput
          label="Suffix for clashing right columns"
          value={String(config.suffix ?? "_2")}
          disabled={!editable}
          onChange={(e) => onChange("suffix", e.currentTarget.value)}
        />
      </Stack>
    );
  }

  if (kind === "transform.union") {
    return (
      <Select
        label="Columns"
        allowDeselect={false}
        disabled={!editable}
        value={String(config.mode ?? "union")}
        data={[
          { value: "union", label: "Union — keep all columns (fill gaps empty)" },
          { value: "intersection", label: "Intersection — only shared columns" },
        ]}
        onChange={(v) => onChange("mode", v ?? "union")}
      />
    );
  }

  if (kind === "transform.dedupe") {
    return (
      <Stack gap="xs">
        {columnsPicker("Key columns", "Leave empty to match on the whole row")}
        <Select
          label="When rows match, keep"
          allowDeselect={false}
          disabled={!editable}
          value={String(config.keep ?? "first")}
          data={[
            { value: "first", label: "First occurrence" },
            { value: "last", label: "Last occurrence" },
          ]}
          onChange={(v) => onChange("keep", v ?? "first")}
        />
      </Stack>
    );
  }

  if (kind === "transform.split_column") {
    const into = Array.isArray(config.into) ? (config.into as unknown[]).map(String) : [];
    return (
      <Stack gap="xs">
        <Select
          label="Column to split"
          data={opts(in0, String(config.source ?? ""))}
          value={String(config.source ?? "") || null}
          disabled={!editable}
          searchable
          placeholder={in0.length ? "Pick a column" : "Test the source to load columns"}
          onChange={(v) => onChange("source", v ?? "")}
        />
        <TextInput
          label="Separator"
          value={String(config.separator ?? "")}
          disabled={!editable}
          placeholder="e.g. a space, a comma"
          onChange={(e) => onChange("separator", e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          New columns, in order (extra pieces are dropped, missing ones fill empty).
        </Text>
        {into.map((name, i) => (
          <Group key={i} gap={4} wrap="nowrap" align="flex-end">
            <TextInput
              label={i === 0 ? "Into" : undefined}
              value={name}
              disabled={!editable}
              placeholder={`part ${i + 1}`}
              style={{ flex: 1 }}
              onChange={(e) =>
                onChange(
                  "into",
                  into.map((c, idx) => (idx === i ? e.currentTarget.value : c)),
                )
              }
            />
            <ActionIcon
              variant="subtle"
              color="red"
              disabled={!editable}
              aria-label="Remove"
              onClick={() =>
                onChange(
                  "into",
                  into.filter((_, idx) => idx !== i),
                )
              }
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}
        <Button
          size="compact-xs"
          variant="light"
          disabled={!editable}
          onClick={() => onChange("into", [...into, ""])}
        >
          Add column
        </Button>
        <Switch
          label="Keep the original column"
          checked={Boolean(config.keepSource)}
          disabled={!editable}
          onChange={(e) => onChange("keepSource", e.currentTarget.checked)}
        />
      </Stack>
    );
  }

  if (kind === "transform.merge_columns") {
    return (
      <Stack gap="xs">
        {columnsPicker("Columns to merge (in order)")}
        <TextInput
          label="Into column"
          value={String(config.target ?? "")}
          disabled={!editable}
          placeholder="new column name"
          onChange={(e) => onChange("target", e.currentTarget.value)}
        />
        <TextInput
          label="Separator"
          value={String(config.separator ?? "")}
          disabled={!editable}
          placeholder="e.g. a space"
          onChange={(e) => onChange("separator", e.currentTarget.value)}
        />
        <Switch
          label="Keep the source columns"
          checked={config.keepSources !== false}
          disabled={!editable}
          onChange={(e) => onChange("keepSources", e.currentTarget.checked)}
        />
      </Stack>
    );
  }

  if (kind === "transform.date_format") {
    const DATE_PRESETS = [
      { value: "%Y-%m-%d", label: "2026-01-05 (ISO)" },
      { value: "%d/%m/%Y", label: "05/01/2026 (DD/MM/YYYY)" },
      { value: "%m/%d/%Y", label: "01/05/2026 (MM/DD/YYYY)" },
      { value: "%d-%b-%Y", label: "05-Jan-2026" },
      { value: "%Y-%m-%dT%H:%M:%SZ", label: "2026-01-05T00:00:00Z" },
    ];
    const fmt = String(config.format ?? "");
    const isPreset = DATE_PRESETS.some((p) => p.value === fmt);
    return (
      <Stack gap="xs">
        <Select
          label="Column"
          data={opts(in0, String(config.column ?? ""))}
          value={String(config.column ?? "") || null}
          disabled={!editable}
          searchable
          placeholder={in0.length ? "Pick a column" : "Test the source to load columns"}
          onChange={(v) => onChange("column", v ?? "")}
        />
        <Select
          label="Output format"
          allowDeselect={false}
          disabled={!editable}
          value={isPreset ? fmt : "__custom__"}
          data={[...DATE_PRESETS, { value: "__custom__", label: "Custom…" }]}
          onChange={(v) => onChange("format", v === "__custom__" ? fmt : (v ?? "%Y-%m-%d"))}
        />
        {!isPreset && (
          <TextInput
            label="Custom pattern (strftime)"
            value={fmt}
            disabled={!editable}
            placeholder="%d-%b-%Y"
            onChange={(e) => onChange("format", e.currentTarget.value)}
          />
        )}
        <TextInput
          label="Write to column (optional)"
          value={String(config.target ?? "")}
          disabled={!editable}
          placeholder="leave empty to overwrite in place"
          onChange={(e) => onChange("target", e.currentTarget.value)}
        />
        <Select
          label="If a cell isn't a date"
          allowDeselect={false}
          disabled={!editable}
          value={String(config.onError ?? "keep")}
          data={[
            { value: "keep", label: "Keep the original" },
            { value: "null", label: "Set it empty" },
          ]}
          onChange={(v) => onChange("onError", v ?? "keep")}
        />
      </Stack>
    );
  }

  if (kind === "transform.conditional") {
    const cases = Array.isArray(config.cases)
      ? (config.cases as { column?: string; op?: string; value?: string; result?: string }[])
      : [];
    const setCase = (i: number, key: "column" | "op" | "value" | "result", val: string) =>
      onChange(
        "cases",
        cases.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)),
      );
    return (
      <Stack gap="xs">
        <TextInput
          label="Write to column"
          value={String(config.target ?? "")}
          disabled={!editable}
          placeholder="new or existing column"
          onChange={(e) => onChange("target", e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          The first matching rule wins; if none match, the default is used.
        </Text>
        {cases.map((c, i) => {
          const op = String(c.op ?? "eq");
          const needsValue = op !== "empty" && op !== "not_empty";
          return (
            <Paper key={i} withBorder p={6} radius="sm">
              <Stack gap={4}>
                <Group gap={4} justify="space-between">
                  <Text size="xs" fw={600}>
                    {i === 0 ? "If" : "Else if"}
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    disabled={!editable}
                    aria-label="Remove rule"
                    onClick={() =>
                      onChange(
                        "cases",
                        cases.filter((_, idx) => idx !== i),
                      )
                    }
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
                <Select
                  data={opts(in0, String(c.column ?? ""))}
                  value={String(c.column ?? "") || null}
                  disabled={!editable}
                  searchable
                  placeholder="column"
                  onChange={(v) => setCase(i, "column", v ?? "")}
                />
                <Select
                  allowDeselect={false}
                  disabled={!editable}
                  value={op}
                  data={[
                    { value: "eq", label: "equals" },
                    { value: "ne", label: "does not equal" },
                    { value: "contains", label: "contains" },
                    { value: "not_contains", label: "does not contain" },
                    { value: "not_empty", label: "is not empty" },
                    { value: "empty", label: "is empty" },
                    { value: "gt", label: "> (number)" },
                    { value: "lt", label: "< (number)" },
                    { value: "gte", label: ">= (number)" },
                    { value: "lte", label: "<= (number)" },
                  ]}
                  onChange={(v) => setCase(i, "op", v ?? "eq")}
                />
                {needsValue && (
                  <TextInput
                    value={String(c.value ?? "")}
                    disabled={!editable}
                    placeholder="value"
                    onChange={(e) => setCase(i, "value", e.currentTarget.value)}
                  />
                )}
                <TextInput
                  label="then set to"
                  value={String(c.result ?? "")}
                  disabled={!editable}
                  onChange={(e) => setCase(i, "result", e.currentTarget.value)}
                />
              </Stack>
            </Paper>
          );
        })}
        <Button
          size="compact-xs"
          variant="light"
          disabled={!editable}
          onClick={() =>
            onChange("cases", [...cases, { column: "", op: "eq", value: "", result: "" }])
          }
        >
          Add rule
        </Button>
        <TextInput
          label="Default (no rule matched)"
          value={String(config.default ?? "")}
          disabled={!editable}
          onChange={(e) => onChange("default", e.currentTarget.value)}
        />
      </Stack>
    );
  }

  if (kind === "transform.unpivot") {
    const idCols = Array.isArray(config.idColumns)
      ? (config.idColumns as unknown[]).map(String)
      : [];
    const valCols = Array.isArray(config.valueColumns)
      ? (config.valueColumns as unknown[]).map(String)
      : [];
    return (
      <Stack gap="xs">
        <MultiSelect
          label="ID columns (kept on every row)"
          data={opts(in0, ...idCols)}
          value={idCols}
          disabled={!editable}
          searchable
          clearable
          placeholder={in0.length ? "Pick columns" : "Test the source to load columns"}
          onChange={(v) => onChange("idColumns", v)}
        />
        <MultiSelect
          label="Value columns (each becomes rows)"
          data={opts(in0, ...valCols)}
          value={valCols}
          disabled={!editable}
          searchable
          clearable
          placeholder={in0.length ? "Pick columns" : "Test the source to load columns"}
          onChange={(v) => onChange("valueColumns", v)}
        />
        <TextInput
          label="Name column"
          value={String(config.varName ?? "variable")}
          disabled={!editable}
          onChange={(e) => onChange("varName", e.currentTarget.value)}
        />
        <TextInput
          label="Value column"
          value={String(config.valueName ?? "value")}
          disabled={!editable}
          onChange={(e) => onChange("valueName", e.currentTarget.value)}
        />
      </Stack>
    );
  }

  if (kind === "transform.explode") {
    return (
      <Stack gap="xs">
        <Select
          label="Column to explode"
          data={opts(in0, String(config.column ?? ""))}
          value={String(config.column ?? "") || null}
          disabled={!editable}
          searchable
          placeholder={in0.length ? "Pick a column" : "Test the source to load columns"}
          onChange={(v) => onChange("column", v ?? "")}
        />
        <TextInput
          label="Separator (for text cells)"
          value={String(config.separator ?? "")}
          disabled={!editable}
          placeholder=","
          onChange={(e) => onChange("separator", e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          Each value becomes its own row; the other columns are copied. A list cell explodes
          element-wise.
        </Text>
      </Stack>
    );
  }

  return null;
}

function NodeInspector({
  node,
  editable,
  onChange,
  onToggle,
  onDelete,
  upstream,
  inputColumns,
  onColumns,
  projectId,
  pipelineId,
  lifecycle,
  getGraph,
}: {
  node: Node;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  onToggle: (disabled: boolean) => void;
  onDelete: () => void;
  /** Which project this canvas belongs to, and which pipeline it is — the Run
   *  Pipeline node offers the project's other pipelines, never this one. */
  projectId: string;
  pipelineId: string;
  /** This pipeline's lifecycle state: it bounds what it may run. */
  lifecycle: string;
  /** What the nearest upstream Veeva Source reads, when there is one — so a
   *  target need not be told what the graph already says. */
  upstream: {
    kind: "veeva_document" | "veeva_object";
    object: string | null;
    connectionId: string | null;
  } | null;
  inputColumns: Record<string, string[]>;
  onColumns: (nodeId: string, cols: string[]) => void;
  /** The on-canvas graph, for nodes whose Test runs their upstream. */
  getGraph: () => Record<string, unknown>;
}) {
  const data = node.data as Record<string, unknown>;
  const kind = String(data.kind ?? "");
  const def = NODE_BY_KIND.get(kind);
  const config = (data.config ?? {}) as Record<string, unknown>;
  const disabled = Boolean(data.disabled);
  const display = data.display as DisplayResult[] | undefined;

  return (
    <Stack gap="xs">
      <Text fw={600}>{def?.label ?? kind}</Text>
      {def?.description && (
        <Text size="xs" c="dimmed">
          {def.description}
        </Text>
      )}

      <Switch
        label="Enabled"
        checked={!disabled}
        disabled={!editable}
        onChange={(e) => onToggle(!e.currentTarget.checked)}
      />

      {kind === "data.set" ? (
        <SetConfig key={node.id} config={config} editable={editable} onChange={onChange} />
      ) : kind === "data.table" ? (
        <TableConfig key={node.id} config={config} editable={editable} onChange={onChange} />
      ) : kind === "trigger.blob" ? (
        <BlobTriggerConfig key={node.id} config={config} editable={editable} onChange={onChange} />
      ) : kind === "blob.sink" ? (
        <BlobSinkConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          inputColumns={inputColumns}
          upstream={upstream}
        />
      ) : kind === "s3.sink" ? (
        <S3SinkConfig key={node.id} config={config} editable={editable} onChange={onChange} />
      ) : kind === "veeva.sink" ? (
        <VeevaSinkConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          inputColumns={inputColumns}
        />
      ) : kind === "veeva.staging" ? (
        <VeevaStagingSinkConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
        />
      ) : kind === "blob.source" ? (
        <BlobSourceConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          onColumns={(cols) => onColumns(node.id, cols)}
          // Passed in, not imported there: importing DataView back out of this
          // file would make the two modules circular.
          renderPreview={(rows) => <DataView data={rows} />}
        />
      ) : kind === "cara.source" ? (
        <CaraSourceConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          onColumns={(cols) => onColumns(node.id, cols)}
        />
      ) : kind === "s3.source" ? (
        <S3SourceConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          onColumns={(cols) => onColumns(node.id, cols)}
          // Passed in rather than imported there: the inspector lives in its
          // own file, and importing DataView back out of this one would make
          // the two modules circular.
          renderPreview={(rows) => <DataView data={rows} />}
        />
      ) : kind === "trigger.s3" ? (
        <S3TriggerConfig key={node.id} config={config} editable={editable} onChange={onChange} />
      ) : kind === "db.snapshot" ? (
        <DbSnapshotConfig key={node.id} config={config} editable={editable} onChange={onChange} />
      ) : kind === "archive.sink" ? (
        <ArchiveSinkConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          inputColumns={inputColumns}
        />
      ) : kind === "archive.dataset_sink" ? (
        <ArchiveDatasetSinkConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
        />
      ) : kind === "archive.ensure_container" ? (
        <ArchiveEnsureContainerConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
        />
      ) : kind === "archive.source" ? (
        <ArchiveSourceConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          onColumns={(cols) => onColumns(node.id, cols)}
        />
      ) : kind === "archive.disposition" || kind === "archive.verify" ? (
        <ArchiveReviewConfig
          key={node.id}
          kind={kind}
          config={config}
          editable={editable}
          onChange={onChange}
        />
      ) : kind === "flow.undo" ? (
        <UndoConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          lifecycleState={lifecycle}
        />
      ) : kind === "veeva.lookup" ? (
        <LookupConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          inputColumns={inputColumns}
        />
      ) : kind === "validate.compare" ? (
        <CompareConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          inputColumns={inputColumns}
        />
      ) : kind === "validate.reconcile" ? (
        <ReconcileConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          inputColumns={inputColumns}
        />
      ) : kind === "validate.rows" ? (
        <ValidateConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          inputColumns={inputColumns}
        />
      ) : kind === "ai.predict_metadata" ? (
        <PredictMetadataConfig
          key={node.id}
          nodeId={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          inputColumns={inputColumns}
          pipelineId={pipelineId}
          getGraph={getGraph}
        />
      ) : kind === "flow.approval" ? (
        <ApprovalConfig
          config={config}
          editable={editable}
          onChange={onChange}
          lifecycleState={lifecycle}
        />
      ) : kind === "flow.run_pipeline" ? (
        <RunPipelineConfig
          config={config}
          editable={editable}
          onChange={onChange}
          projectId={projectId}
          selfPipelineId={pipelineId}
          lifecycleState={lifecycle}
        />
      ) : kind === "veeva.query" ? (
        <VeevaQueryConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          onColumns={(cols) => onColumns(node.id, cols)}
        />
      ) : kind === "database.source" ? (
        <DatabaseSourceConfig
          key={node.id}
          config={config}
          editable={editable}
          onChange={onChange}
          onColumns={(cols) => onColumns(node.id, cols)}
        />
      ) : kind.startsWith("transform.") ? (
        <TransformConfig
          key={node.id}
          kind={kind}
          config={config}
          editable={editable}
          onChange={onChange}
          inputColumns={inputColumns}
        />
      ) : def && def.configFields.length > 0 ? (
        def.configFields.map((f) =>
          f.type === "number" ? (
            <NumberInput
              key={f.key}
              label={f.label}
              min={f.min ?? 1}
              max={f.max}
              value={Number(config[f.key] ?? f.min ?? 1)}
              disabled={!editable}
              onChange={(v) =>
                onChange(f.key, typeof v === "number" ? v : Number(v) || (f.min ?? 1))
              }
            />
          ) : f.type === "select" ? (
            <Select
              key={f.key}
              label={f.label}
              data={f.options ?? []}
              value={String(config[f.key] ?? f.options?.[0]?.value ?? "")}
              disabled={!editable}
              allowDeselect={false}
              onChange={(v) => v && onChange(f.key, v)}
            />
          ) : (
            <TextInput
              key={f.key}
              label={f.label}
              placeholder={f.placeholder}
              value={String(config[f.key] ?? "")}
              disabled={!editable}
              onChange={(e) => onChange(f.key, e.currentTarget.value)}
            />
          ),
        )
      ) : null}

      {def?.category === "display" && Array.isArray(display) && display.length > 0 && (
        <Stack gap={4} mt="xs">
          <Text size="xs" tt="uppercase" fw={600} c="dimmed">
            Output (last run)
          </Text>
          {display.map((r) => (
            <Stack key={r.port} gap={2}>
              <Text size="xs" c="dimmed">
                Input {Number(r.port.replace("in-", "")) + 1}
              </Text>
              <DataView data={r.data} />
            </Stack>
          ))}
        </Stack>
      )}

      <Button
        color="red"
        variant="light"
        size="xs"
        mt="sm"
        leftSection={<IconTrash size={14} />}
        disabled={!editable}
        onClick={onDelete}
      >
        Delete node
      </Button>
    </Stack>
  );
}

/** Typed, idiot-proof editor for the Constant (data.set) node: Number / Text /
 *  Boolean can't be malformed; JSON is live-validated and never stores invalid
 *  syntax. Keyed by node id in the inspector so its JSON text re-inits per node. */
function SetConfig({
  config,
  editable,
  onChange,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  const valueType = String(config.valueType ?? "number");
  const value = config.value;
  const [jsonText, setJsonText] = useState<string>(() => {
    try {
      return JSON.stringify(value ?? {}, null, 2);
    } catch {
      return "{}";
    }
  });
  const [jsonError, setJsonError] = useState<string | null>(null);

  const setType = (t: string) => {
    const fresh = t === "number" ? 0 : t === "boolean" ? false : t === "json" ? {} : "";
    onChange("valueType", t);
    onChange("value", fresh);
    if (t === "json") {
      setJsonText(JSON.stringify(fresh, null, 2));
      setJsonError(null);
    }
  };

  return (
    <Stack gap="xs">
      <Select
        label="Value type"
        value={valueType}
        disabled={!editable}
        allowDeselect={false}
        data={[
          { value: "number", label: "Number" },
          { value: "text", label: "Text" },
          { value: "boolean", label: "Boolean" },
          { value: "json", label: "JSON" },
        ]}
        onChange={(v) => v && setType(v)}
      />
      {valueType === "number" && (
        <NumberInput
          label="Value"
          value={typeof value === "number" ? value : Number(value) || 0}
          disabled={!editable}
          onChange={(v) => onChange("value", typeof v === "number" ? v : Number(v) || 0)}
        />
      )}
      {valueType === "text" && (
        <TextInput
          label="Value"
          value={typeof value === "string" ? value : String(value ?? "")}
          disabled={!editable}
          onChange={(e) => onChange("value", e.currentTarget.value)}
        />
      )}
      {valueType === "boolean" && (
        <Switch
          label={value ? "true" : "false"}
          checked={Boolean(value)}
          disabled={!editable}
          onChange={(e) => onChange("value", e.currentTarget.checked)}
        />
      )}
      {valueType === "json" && (
        <Textarea
          label="Value (JSON)"
          autosize
          minRows={3}
          value={jsonText}
          disabled={!editable}
          error={jsonError ?? undefined}
          onChange={(e) => {
            const t = e.currentTarget.value;
            setJsonText(t);
            try {
              const parsed: unknown = JSON.parse(t);
              setJsonError(null);
              onChange("value", parsed);
            } catch {
              setJsonError("Invalid JSON — fix the syntax to save this value.");
            }
          }}
        />
      )}
    </Stack>
  );
}

/** Create Table config: a small spreadsheet-style editor. Define columns
 *  (header row) and type rows into the grid; the node outputs one object per
 *  row keyed by the column names — a hand-built dataframe. All cells are text
 *  (a downstream Convert Type node opts into real types), matching how sources
 *  emit data. New columns get a unique default name so a column is never blank
 *  by accident; blank/duplicate names are flagged rather than blocked. */
function TableConfig({
  config,
  editable,
  onChange,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  const columns = Array.isArray(config.columns) ? (config.columns as unknown[]).map(String) : [];
  const rows: string[][] = Array.isArray(config.rows)
    ? (config.rows as unknown[]).map((r) =>
        Array.isArray(r) ? (r as unknown[]).map((c) => String(c ?? "")) : [],
      )
    : [];

  const nextName = (): string => {
    const used = new Set(columns);
    let n = columns.length + 1;
    while (used.has(`column_${n}`)) n += 1;
    return `column_${n}`;
  };
  const setColumns = (c: string[]) => onChange("columns", c);
  const setRows = (r: string[][]) => onChange("rows", r);

  const renameColumn = (i: number, name: string) =>
    setColumns(columns.map((c, idx) => (idx === i ? name : c)));
  const addColumn = () => {
    setColumns([...columns, nextName()]);
    setRows(rows.map((r) => [...r, ""]));
  };
  const deleteColumn = (i: number) => {
    setColumns(columns.filter((_, idx) => idx !== i));
    setRows(rows.map((r) => r.filter((_, idx) => idx !== i)));
  };
  const addRow = () => setRows([...rows, columns.map(() => "")]);
  const deleteRow = (r: number) => setRows(rows.filter((_, idx) => idx !== r));
  const setCell = (r: number, c: number, val: string) =>
    setRows(
      rows.map((row, ri) => (ri === r ? row.map((cell, ci) => (ci === c ? val : cell)) : row)),
    );

  // Idiot-proof feedback: blank/duplicate column names make a confusing table
  // (duplicate keys collapse). Flag them without blocking mid-edit typing.
  const dupes = [...new Set(columns.filter((c, i) => c && columns.indexOf(c) !== i))];
  const hasBlank = columns.some((c) => !c.trim());
  const CELL_W = 110;

  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        Build a table by hand. Values are text — add a Convert Type node to set number / date /
        boolean.
      </Text>
      <div style={{ overflowX: "auto", maxWidth: "100%" }}>
        <Stack gap={4} style={{ width: "min-content" }}>
          <Group gap={4} wrap="nowrap" align="center">
            {columns.map((c, ci) => (
              <TextInput
                key={ci}
                size="xs"
                w={CELL_W}
                value={c}
                placeholder="column"
                disabled={!editable}
                onChange={(e) => renameColumn(ci, e.currentTarget.value)}
                rightSection={
                  columns.length > 1 && (
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="red"
                      disabled={!editable}
                      aria-label="Delete column"
                      onClick={() => deleteColumn(ci)}
                    >
                      <IconTrash size={12} />
                    </ActionIcon>
                  )
                }
              />
            ))}
            <ActionIcon
              variant="light"
              disabled={!editable}
              aria-label="Add column"
              onClick={addColumn}
            >
              <IconPlus size={14} />
            </ActionIcon>
          </Group>
          {rows.map((row, ri) => (
            <Group key={ri} gap={4} wrap="nowrap" align="center">
              {columns.map((_, ci) => (
                <TextInput
                  key={ci}
                  size="xs"
                  w={CELL_W}
                  value={row[ci] ?? ""}
                  disabled={!editable}
                  onChange={(e) => setCell(ri, ci, e.currentTarget.value)}
                />
              ))}
              <ActionIcon
                variant="subtle"
                color="red"
                disabled={!editable || rows.length <= 1}
                aria-label="Delete row"
                onClick={() => deleteRow(ri)}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
      </div>
      <Button
        size="compact-xs"
        variant="light"
        disabled={!editable}
        leftSection={<IconPlus size={14} />}
        onClick={addRow}
      >
        Add row
      </Button>
      {hasBlank && (
        <Text size="xs" c="orange">
          Every column needs a name.
        </Text>
      )}
      {dupes.length > 0 && (
        <Text size="xs" c="orange">
          Duplicate column names: {dupes.join(", ")}. Later columns overwrite earlier ones.
        </Text>
      )}
    </Stack>
  );
}

/** Idiot-proof Blob-arrival config: pick one of the tenant's Azure Blob
 *  connections, then browse to the folder to watch. No free-text paths — the
 *  container is a dropdown and folders are drilled into by clicking (the blob
 *  list is flat, so subfolders are derived client-side by splitting on "/").
 *  The watched folder is the current prefix. */
function BlobTriggerConfig({
  config,
  editable,
  onChange,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  const getToken = useAccessToken();
  // A node names a connection id or a `@role`; everything here takes an id.
  const {
    connectionId,
    role: roleBlobTrigger,
    settled: settledBlobTrigger,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const container = String(config.container ?? "");
  const prefix = String(config.prefix ?? "");

  const [connections, setConnections] = useState<{ value: string; label: string }[]>([]);
  const [containers, setContainers] = useState<string[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Load the tenant's Blob connections once.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await listConnections(token, "blob")).items;
        if (!cancelled) {
          setConnections(items.map((c) => ({ value: c.connection_id, label: c.display_name })));
        }
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  // Load containers when a connection is chosen.
  useEffect(() => {
    if (!connectionId) {
      setContainers([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await blobListContainers(token, connectionId)).items;
        if (!cancelled) setContainers(items.map((c) => c.name));
      } catch (err) {
        toastApiError("Could not load containers", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  // List blobs under the current folder and derive the immediate subfolders.
  useEffect(() => {
    if (!connectionId || !container) {
      setFolders([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const token = await getToken();
        const items = (await blobListFiles(token, connectionId, container, prefix || null)).items;
        const subs = new Set<string>();
        for (const b of items) {
          const rest = b.name.startsWith(prefix) ? b.name.slice(prefix.length) : b.name;
          const slash = rest.indexOf("/");
          if (slash > 0) subs.add(rest.slice(0, slash));
        }
        if (!cancelled) setFolders([...subs].sort());
      } catch (err) {
        toastApiError("Could not browse folder", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, container, prefix]);

  const segments = prefix ? prefix.replace(/\/$/, "").split("/") : [];
  const goToSegment = (i: number) => onChange("prefix", segments.slice(0, i + 1).join("/") + "/");

  return (
    <Stack gap="xs">
      <ConnectionRefSelect
        label="Blob connection"
        placeholder={connections.length ? "Pick a connection" : "No Blob connections set up"}
        data={connections}
        nothingFoundMessage="No Blob connections"
        connectionId={connectionId}
        role={roleBlobTrigger}
        settled={settledBlobTrigger}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.value === v)?.label ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          onChange("container", "");
          onChange("prefix", "");
        }}
      />

      {connectionId && (
        <Select
          label="Container"
          placeholder="Pick a container"
          data={containers}
          value={container || null}
          disabled={!editable}
          searchable
          onChange={(v) => {
            onChange("container", v ?? "");
            onChange("prefix", "");
          }}
        />
      )}

      {connectionId && container && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            Folder to watch
          </Text>
          {/* Breadcrumb: click a crumb to jump up. */}
          <Group gap={4} wrap="wrap">
            <Anchor size="xs" onClick={() => editable && onChange("prefix", "")}>
              {container}
            </Anchor>
            {segments.map((seg, i) => (
              <Group key={i} gap={4} wrap="nowrap">
                <Text size="xs" c="dimmed">
                  /
                </Text>
                <Anchor size="xs" onClick={() => editable && goToSegment(i)}>
                  {seg}
                </Anchor>
              </Group>
            ))}
          </Group>
          {/* Subfolders to drill into. */}
          {loading ? (
            <Loader size="xs" />
          ) : folders.length > 0 ? (
            <Stack gap={2}>
              {folders.map((f) => (
                <Button
                  key={f}
                  variant="subtle"
                  size="compact-xs"
                  justify="flex-start"
                  disabled={!editable}
                  onClick={() => onChange("prefix", `${prefix}${f}/`)}
                >
                  📁 {f}
                </Button>
              ))}
            </Stack>
          ) : (
            <Text size="xs" c="dimmed">
              No subfolders here — this folder will be watched.
            </Text>
          )}
          <Text size="xs" mt={4}>
            Watching:{" "}
            <Text span ff="monospace">
              {container}/{prefix || ""}
            </Text>
          </Text>
        </Stack>
      )}
    </Stack>
  );
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Rows to render as a table, if the data is tabular: an array of flat objects,
 *  or an object with a `rows` array of flat objects (the Veeva Source shape).
 *  Returns null when the data isn't tabular (then only JSON is shown). */
/** Union of column names across rows, first-seen order. */
function columnsOf(rows: Record<string, unknown>[]): string[] {
  const seen = new Set<string>();
  const cols: string[] = [];
  for (const r of rows) {
    for (const k of Object.keys(r)) {
      if (!seen.has(k)) {
        seen.add(k);
        cols.push(k);
      }
    }
  }
  return cols;
}

function tabularRows(data: unknown): Record<string, unknown>[] | null {
  const isFlatRow = (d: unknown): d is Record<string, unknown> =>
    !!d && typeof d === "object" && !Array.isArray(d);
  if (Array.isArray(data) && data.length > 0 && data.every(isFlatRow)) {
    return data as Record<string, unknown>[];
  }
  if (data && typeof data === "object" && Array.isArray((data as { rows?: unknown }).rows)) {
    const rows = (data as { rows: unknown[] }).rows;
    if (rows.length > 0 && rows.every(isFlatRow)) return rows as Record<string, unknown>[];
  }
  return null;
}

/** Renders a Display value as Table + JSON in two tabs. Defaults to Table when
 *  the data is tabular, else JSON.
 *
 *  Exported for its own test: it is the one piece of this file with logic worth
 *  pinning independently of the canvas around it. */
export function DataView({ data }: { data: unknown }) {
  const rows = tabularRows(data);
  const columns = rows ? [...new Set(rows.flatMap((r) => Object.keys(r)))] : [];
  const shown = rows?.slice(0, 50) ?? [];
  return (
    <Tabs defaultValue={rows ? "table" : "json"} variant="outline">
      <Tabs.List>
        <Tabs.Tab value="table">Table</Tabs.Tab>
        <Tabs.Tab value="json">JSON</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="table" pt={4}>
        {rows ? (
          <div style={{ overflowX: "auto" }}>
            {/* Shape before content: "how much is this?" is the first question
                a dataframe raises, and reading it off the table means counting
                — impossible once the row list is capped. */}
            <Text size="xs" c="dimmed" mb={4}>
              {rows.length.toLocaleString()} {rows.length === 1 ? "row" : "rows"} × {columns.length}{" "}
              {columns.length === 1 ? "column" : "columns"}
              {rows.length > shown.length ? ` · showing first ${shown.length}` : ""}
            </Text>
            <Table striped withTableBorder verticalSpacing={4} fz="xs">
              <Table.Thead>
                <Table.Tr>
                  {columns.map((c) => (
                    <Table.Th key={c}>{c}</Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {shown.map((r, i) => (
                  <Table.Tr key={i}>
                    {columns.map((c) => (
                      <Table.Td key={c}>{cellText(r[c])}</Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        ) : (
          <Text size="xs" c="dimmed">
            Not tabular — see the JSON tab.
          </Text>
        )}
      </Tabs.Panel>
      <Tabs.Panel value="json" pt={4}>
        <Group justify="flex-end" mb={4}>
          <CopyButton value={JSON.stringify(data, null, 2)}>
            {({ copied, copy }) => (
              <Button
                size="xs"
                variant="light"
                color={copied ? "teal" : "gray"}
                leftSection={<IconCopy size={14} />}
                onClick={copy}
              >
                {copied ? "Copied" : "Copy JSON"}
              </Button>
            )}
          </CopyButton>
        </Group>
        <Code block>{JSON.stringify(data, null, 2)}</Code>
      </Tabs.Panel>
    </Tabs>
  );
}

/** Save-to-Blob (blob.sink) config.
 *
 *  Starts from **what** is being saved — a table, Veeva documents, or Veeva
 *  object records — because everything else follows from that: a table needs a
 *  format and a filename, Veeva data needs a vault and a list of artefacts.
 *
 *  `upstream` is what the nearest Veeva Source reads. Where the graph can
 *  answer a question, the form does not ask it.
 *
 *  Exported for its own test: the form is conditional enough that "does object
 *  mode ever mention documents" is worth asserting rather than eyeballing. */
export function BlobSinkConfig({
  config,
  editable,
  onChange,
  inputColumns,
  upstream,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  inputColumns: Record<string, string[]>;
  upstream: {
    kind: "veeva_document" | "veeva_object";
    object: string | null;
    connectionId: string | null;
  } | null;
}) {
  const getToken = useAccessToken();
  // A node names a connection id or a `@role`; everything here takes an id.
  const {
    connectionId,
    role: roleBlobSink,
    settled: settledBlobSink,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const container = String(config.container ?? "");
  const prefix = String(config.prefix ?? "");
  const filename = String(config.filename ?? "");
  const format = String(config.format ?? "csv");
  const dataKind = String(config.dataKind ?? "table");
  const isTable = dataKind === "table";
  const isDocument = dataKind === "veeva_document";
  const isObject = dataKind === "veeva_object";
  const idColumn = String(config.idColumn ?? "id");
  const folderNameColumns = Array.isArray(config.folderNameColumns)
    ? (config.folderNameColumns as string[])
    : [idColumn];
  const pdfColumns = Array.isArray(config.pdfColumns) ? (config.pdfColumns as string[]) : [];
  // The folder browser leaves a trailing slash, but a prefix typed or imported
  // into the config need not have one — and the engine appends it either way,
  // so the preview has to as well or it shows a path the run will not write.
  const folderPrefix = prefix && !prefix.endsWith("/") ? `${prefix}/` : prefix;
  // The graph already says which object the rows came from — it is the upstream
  // query's FROM clause. Asking for it again is work the tool should do.
  const upstreamObject = upstream?.kind === "veeva_object" ? upstream.object : null;
  const cols = inputColumns["in-0"] ?? [];

  const [newContainer, setNewContainer] = useState("");
  const [creating, setCreating] = useState(false);

  const [connections, setConnections] = useState<{ value: string; label: string }[]>([]);
  const [containers, setContainers] = useState<string[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await listConnections(token, "blob")).items;
        if (!cancelled)
          setConnections(items.map((c) => ({ value: c.connection_id, label: c.display_name })));
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (!connectionId) {
      setContainers([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await blobListContainers(token, connectionId)).items;
        if (!cancelled) setContainers(items.map((c) => c.name));
      } catch (err) {
        toastApiError("Could not load containers", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  useEffect(() => {
    if (!connectionId || !container) {
      setFolders([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const token = await getToken();
        const items = (await blobListFiles(token, connectionId, container, prefix || null)).items;
        const subs = new Set<string>();
        for (const b of items) {
          const rest = b.name.startsWith(prefix) ? b.name.slice(prefix.length) : b.name;
          const slash = rest.indexOf("/");
          if (slash > 0) subs.add(rest.slice(0, slash));
        }
        if (!cancelled) setFolders([...subs].sort());
      } catch (err) {
        toastApiError("Could not browse folder", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, container, prefix]);

  // No Veeva connection picker and no object picker here, deliberately. The
  // Veeva Source wired upstream holds both — the vault it read and the object
  // its query named — and the run takes them from its output. Asking again was
  // the tool making the operator restate what the graph already carries.

  const createContainer = async () => {
    const name = newContainer.trim();
    if (!name || !connectionId) return;
    setCreating(true);
    try {
      const token = await getToken();
      await blobCreateContainer(token, connectionId, name, "pipeline save-to-blob destination");
      setContainers((prev) => (prev.includes(name) ? prev : [...prev, name]));
      onChange("container", name);
      onChange("prefix", "");
      setNewContainer("");
      toastSuccess("Container created", name);
    } catch (err) {
      toastApiError("Could not create the container", err);
    } finally {
      setCreating(false);
    }
  };

  const segments = prefix ? prefix.replace(/\/$/, "").split("/") : [];
  const goToSegment = (i: number) => onChange("prefix", segments.slice(0, i + 1).join("/") + "/");

  return (
    <Stack gap="xs">
      <Select
        label="What am I saving?"
        description="Decides the shape on disk: one file for a table, or one folder per record for Veeva data."
        allowDeselect={false}
        disabled={!editable}
        value={dataKind}
        data={[
          { value: "table", label: "Table (one file)" },
          { value: "veeva_document", label: "Veeva documents (folder per document)" },
          { value: "veeva_object", label: "Veeva object records (folder per record)" },
        ]}
        onChange={(v) => {
          onChange("dataKind", v ?? "table");
          // Back to the Veeva id column. The id column is kind-specific — a
          // document is reached by its numeric Vault id, a record by its
          // record id — so a column chosen for the previous kind is almost
          // always wrong for the new one, and every fetch would then be made
          // against an identifier Vault does not recognise.
          onChange("idColumn", "id");
        }}
      />

      <ConnectionRefSelect
        label="Blob connection"
        placeholder={connections.length ? "Pick a connection" : "No Blob connections set up"}
        data={connections}
        nothingFoundMessage="No Blob connections"
        connectionId={connectionId}
        role={roleBlobSink}
        settled={settledBlobSink}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.value === v)?.label ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          onChange("container", "");
          onChange("prefix", "");
        }}
      />

      {isTable && (
        <>
          <Select
            label="File format"
            allowDeselect={false}
            disabled={!editable}
            value={format}
            data={[
              { value: "csv", label: "CSV" },
              { value: "tsv", label: "TSV" },
              { value: "json", label: "JSON" },
              { value: "jsonl", label: "JSONL" },
              { value: "xlsx", label: "Excel (.xlsx)" },
            ]}
            onChange={(v) => onChange("format", v ?? "csv")}
          />
          {format === "csv" && (
            <Select
              label="Delimiter"
              allowDeselect={false}
              disabled={!editable}
              value={String(config.delimiter ?? "") || ","}
              data={[
                { value: ",", label: "Comma  ," },
                { value: ";", label: "Semicolon  ;" },
                { value: "\t", label: "Tab" },
                { value: "|", label: "Pipe  |" },
              ]}
              onChange={(v) => onChange("delimiter", v ?? ",")}
            />
          )}
        </>
      )}

      {connectionId && (
        <Select
          label="Container"
          placeholder="Pick a container"
          data={containers}
          value={container || null}
          disabled={!editable}
          searchable
          onChange={(v) => {
            onChange("container", v ?? "");
            onChange("prefix", "");
          }}
        />
      )}

      {/* Creating the destination is part of choosing it — otherwise you leave
          the canvas for the Blob browser and come back. */}
      {connectionId && (
        <Group gap="xs" align="flex-end">
          <TextInput
            label="Create a container"
            placeholder="new-container-name"
            value={newContainer}
            disabled={!editable || creating}
            onChange={(e) => setNewContainer(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Button
            variant="light"
            size="sm"
            loading={creating}
            disabled={!editable || !newContainer.trim()}
            onClick={() => void createContainer()}
          >
            Create
          </Button>
        </Group>
      )}

      {isTable && connectionId && container && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            Folder
          </Text>
          <Group gap={4} wrap="wrap">
            <Anchor size="xs" onClick={() => editable && onChange("prefix", "")}>
              {container}
            </Anchor>
            {segments.map((seg, i) => (
              <Group key={i} gap={4} wrap="nowrap">
                <Text size="xs" c="dimmed">
                  /
                </Text>
                <Anchor size="xs" onClick={() => editable && goToSegment(i)}>
                  {seg}
                </Anchor>
              </Group>
            ))}
          </Group>
          {loading ? (
            <Loader size="xs" />
          ) : folders.length > 0 ? (
            <Stack gap={2}>
              {folders.map((f) => (
                <Button
                  key={f}
                  variant="subtle"
                  size="compact-xs"
                  justify="flex-start"
                  disabled={!editable}
                  onClick={() => onChange("prefix", `${prefix}${f}/`)}
                >
                  📁 {f}
                </Button>
              ))}
            </Stack>
          ) : (
            <Text size="xs" c="dimmed">
              No subfolders here — the file will be saved in this folder.
            </Text>
          )}
        </Stack>
      )}

      {isTable && connectionId && container && (
        <TextInput
          label="File name"
          placeholder={`e.g. output.${format === "xlsx" ? "xlsx" : format}`}
          value={filename}
          disabled={!editable}
          onChange={(e) => onChange("filename", e.currentTarget.value)}
        />
      )}

      {isTable && connectionId && container && filename && (
        <Text size="xs" mt={4}>
          Saving to:{" "}
          <Text span ff="monospace">
            {container}/{prefix}
            {filename}
          </Text>
        </Text>
      )}

      {!isTable && (
        <Stack gap="xs">
          {/* Where the files come from — stated, never asked. The upstream Veeva
              Source is the answer to both questions. */}
          {upstream ? (
            <Text size="xs" c="dimmed">
              Files come from the Veeva Source upstream
              {upstreamObject ? ` · ${upstreamObject}` : ""}.
            </Text>
          ) : (
            <Alert color="yellow" variant="light" p="xs" title="No Veeva Source upstream">
              <Text size="xs">
                Wire this node to a Veeva Source. The vault and the object come from it — this node
                does not ask for either.
              </Text>
            </Alert>
          )}

          <Select
            label="Record id column"
            description="Which column holds the Veeva id of each record."
            data={cols.length ? cols : [idColumn]}
            value={idColumn}
            disabled={!editable}
            allowDeselect={false}
            searchable
            onChange={(v) => onChange("idColumn", v ?? "id")}
          />

          <MultiSelect
            label="Folder name"
            description="Columns joined by “-”, in the order picked. Include the id to keep folders unique; add a name to make them recognisable in the storage browser."
            placeholder={cols.length ? "Pick columns" : "Test the source to load its columns"}
            data={cols.length ? cols : folderNameColumns}
            value={folderNameColumns}
            disabled={!editable || cols.length === 0}
            searchable
            clearable
            onChange={(v) => onChange("folderNameColumns", v)}
          />

          <Text size="xs" fw={500} mt={4}>
            What to save in each folder
          </Text>
          {isDocument && (
            <>
              <Checkbox
                label="The document file"
                description="The source file, under its own filename."
                checked={config.saveDocument !== false}
                disabled={!editable}
                onChange={(e) => onChange("saveDocument", e.currentTarget.checked)}
              />
              <Checkbox
                label="Viewable rendition"
                description="The PDF Vault renders for viewing — it carries the approval signature page the source file does not. A document Vault has not rendered is skipped, not failed."
                checked={Boolean(config.saveRendition)}
                disabled={!editable}
                onChange={(e) => onChange("saveRendition", e.currentTarget.checked)}
              />
            </>
          )}
          {isObject && (
            <>
              <Checkbox
                label="The record's own data"
                description="That row, written as its own file in its folder."
                checked={config.saveRecordTable !== false}
                disabled={!editable}
                onChange={(e) => onChange("saveRecordTable", e.currentTarget.checked)}
              />
              {config.saveRecordTable !== false && (
                <Select
                  label="Record file format"
                  allowDeselect={false}
                  disabled={!editable}
                  value={String(config.recordFormat ?? "csv")}
                  data={[
                    { value: "csv", label: "CSV" },
                    { value: "xlsx", label: "Excel (.xlsx)" },
                  ]}
                  onChange={(v) => onChange("recordFormat", v ?? "csv")}
                />
              )}
            </>
          )}
          {isObject && (
            <>
              <Checkbox
                label="PDF extract"
                description="The record rendered as a Field / Value PDF — the copy a person files or forwards."
                checked={Boolean(config.saveRecordPdf)}
                disabled={!editable}
                onChange={(e) => onChange("saveRecordPdf", e.currentTarget.checked)}
              />
              {Boolean(config.saveRecordPdf) && (
                <MultiSelect
                  pl="md"
                  label="Columns in the PDF"
                  description="In the order picked. Leave empty for every column — rarely what you want for a document someone reads."
                  placeholder={cols.length ? "Pick columns" : "Test the source to load its columns"}
                  data={cols.length ? cols : pdfColumns}
                  value={pdfColumns}
                  disabled={!editable || cols.length === 0}
                  searchable
                  clearable
                  onChange={(v) => onChange("pdfColumns", v)}
                />
              )}
            </>
          )}
          <Checkbox
            label="Attachments"
            description="Each attachment under its original filename. A record with none is skipped."
            checked={Boolean(config.saveAttachments)}
            disabled={!editable}
            onChange={(e) => onChange("saveAttachments", e.currentTarget.checked)}
          />
          <Checkbox
            label="Audit trail"
            description="The record's history from the vault, as audittrail.csv."
            checked={Boolean(config.saveAuditTrail)}
            disabled={!editable}
            onChange={(e) => onChange("saveAuditTrail", e.currentTarget.checked)}
          />

          <Text size="xs" fw={500} mt={4}>
            And once for the whole run
          </Text>
          <Checkbox
            label="The query result as one table"
            description="Saved beside the record folders, not inside them — it describes the run, not a record. Always every row the query returned, even when the per-record download cap bites."
            checked={Boolean(config.saveResultTable)}
            disabled={!editable}
            onChange={(e) => onChange("saveResultTable", e.currentTarget.checked)}
          />
          {Boolean(config.saveResultTable) && (
            <Group gap="xs" align="flex-end" pl="md">
              <TextInput
                label="File name"
                placeholder="results"
                value={String(config.resultFilename ?? "")}
                disabled={!editable}
                onChange={(e) => onChange("resultFilename", e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Select
                label="Format"
                allowDeselect={false}
                disabled={!editable}
                value={String(config.resultFormat ?? "csv")}
                data={[
                  { value: "csv", label: "CSV" },
                  { value: "xlsx", label: "Excel (.xlsx)" },
                ]}
                onChange={(v) => onChange("resultFormat", v ?? "csv")}
                w={140}
              />
            </Group>
          )}

          {container && (
            <Stack gap={2} mt={4}>
              <Text size="xs" c="dimmed">
                One folder per row. Ten records in, ten folders out:
              </Text>
              <Code block>
                {config.saveResultTable
                  ? `${container}/${folderPrefix}${String(config.resultFilename || "results")}.${String(config.resultFormat ?? "csv")}\n`
                  : ""}
                {`${container}/${folderPrefix}<folder name>/`}
                {isDocument && config.saveDocument !== false ? "\n  <the document file>" : ""}
                {isDocument && config.saveRendition ? "\n  viewable_rendition.pdf" : ""}
                {isObject && config.saveRecordTable !== false
                  ? `\n  record.${String(config.recordFormat ?? "csv")}`
                  : ""}
                {isObject && config.saveRecordPdf ? "\n  record.pdf" : ""}
                {config.saveAttachments ? "\n  attachments/<attachment id>-<filename>" : ""}
                {config.saveAuditTrail ? "\n  audittrail.csv" : ""}
              </Code>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/** A column name SQL can read bare, or the quoted form it needs.
 *
 *  A Veeva join produces `id_local-adaptation`, and unquoted that reads as
 *  `id_local` minus `adaptation` — SQLite then reports a column nobody typed.
 *  Quoting is the fix, and the palette below writes it already quoted so the
 *  question does not come up. */
export function sqlIdentifier(column: string): string {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(column) ? column : `"${column.replace(/"/g, '""')}"`;
}

/** SQL Query config: the query, and the columns it can use — click to insert. */
function SqlConfig({
  config,
  editable,
  onChange,
  columns,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  columns: string[];
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const query = String(config.query ?? "");

  // At the cursor, not at the end: the name is usually wanted mid-statement,
  // and making someone retype it after a click would defeat the point.
  const insert = (column: string) => {
    const token = sqlIdentifier(column);
    const area = ref.current;
    const at = area ? (area.selectionStart ?? query.length) : query.length;
    const end = area ? (area.selectionEnd ?? at) : at;
    onChange("query", `${query.slice(0, at)}${token}${query.slice(end)}`);
    // Put the caret after what was just inserted, once React has re-rendered.
    requestAnimationFrame(() => {
      area?.focus();
      area?.setSelectionRange(at + token.length, at + token.length);
    });
  };

  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        Query the input table <Code>input</Code>.
      </Text>
      <Textarea
        label="SQL"
        ref={ref}
        value={query}
        disabled={!editable}
        autosize
        minRows={3}
        maxRows={14}
        styles={{ input: { fontFamily: "monospace" } }}
        onChange={(e) => onChange("query", e.currentTarget.value)}
      />
      {columns.length > 0 && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            Columns — click to insert. Names with a hyphen, space or dot are inserted quoted,
            because SQL reads those as operators.
          </Text>
          <Group gap={4}>
            {columns.map((c) => {
              const needsQuotes = sqlIdentifier(c) !== c;
              return (
                <Badge
                  key={c}
                  size="sm"
                  variant={needsQuotes ? "light" : "outline"}
                  color={needsQuotes ? "orange" : "gray"}
                  style={{
                    cursor: editable ? "pointer" : "default",
                    fontFamily: "var(--mantine-font-family-monospace)",
                    textTransform: "none",
                  }}
                  onClick={() => editable && insert(c)}
                >
                  {c}
                </Badge>
              );
            })}
          </Group>
        </Stack>
      )}
    </Stack>
  );
}

/** Load-to-Veeva-FTP config: a Veeva connection and a staging path, plus the
 *  Blob folder to copy from. No column mapping — it moves whole files, and the
 *  table it produces is what a later node maps. */
export function VeevaStagingSinkConfig({
  config,
  editable,
  onChange,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  const getToken = useAccessToken();
  // Either setting may name a connection id or a `@role`; everything here
  // takes an id.
  const {
    connectionId: veevaConnectionId,
    role: roleStagingVeeva,
    settled: settledStagingVeeva,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const {
    connectionId: blobConnectionId,
    role: roleStagingBlob,
    settled: settledStagingBlob,
  } = useConnectionRef(String(config.fileConnectionId ?? ""));
  const container = String(config.fileContainer ?? "");
  const [veevaConnections, setVeevaConnections] = useState<{ value: string; label: string }[]>([]);
  const [blobConnections, setBlobConnections] = useState<{ value: string; label: string }[]>([]);
  const [containers, setContainers] = useState<string[]>([]);
  const [folders, setFolders] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const [veeva, blob] = await Promise.all([
          listConnections(token, "veeva"),
          listConnections(token, "blob"),
        ]);
        if (cancelled) return;
        setVeevaConnections(
          veeva.items.map((c) => ({ value: c.connection_id, label: c.display_name })),
        );
        setBlobConnections(
          blob.items.map((c) => ({ value: c.connection_id, label: c.display_name })),
        );
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (!blobConnectionId) {
      setContainers([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const items = (await blobListContainers(await getToken(), blobConnectionId)).items;
        if (!cancelled) setContainers(items.map((c) => c.name));
      } catch (err) {
        toastApiError("Could not load containers", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, blobConnectionId]);

  // The folders inside the chosen container, so the source is picked rather
  // than typed — a mistyped prefix silently matches nothing, which looks
  // exactly like an empty folder.
  useEffect(() => {
    if (!blobConnectionId || !container) {
      setFolders([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const items = (await blobListFiles(await getToken(), blobConnectionId, container, null))
          .items;
        const found = new Set<string>();
        for (const b of items) {
          const slash = b.name.indexOf("/");
          if (slash > 0) found.add(b.name.slice(0, slash));
        }
        if (!cancelled) setFolders([...found].sort());
      } catch (err) {
        toastApiError("Could not browse the container", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, blobConnectionId, container]);

  const prefix = String(config.filePrefix ?? "");
  const path = String(config.path ?? "");

  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        Copies files into Vault File Staging so a later Load to Veeva can create documents from
        them. Its output is one row per file, with the staging path in a column called{" "}
        <Text span ff="monospace">
          file
        </Text>
        .
      </Text>

      <ConnectionRefSelect
        label="Veeva connection"
        description="The vault whose File Staging area receives the files."
        placeholder={veevaConnections.length ? "Pick a connection" : "No Veeva connections set up"}
        data={veevaConnections}
        connectionId={veevaConnectionId}
        role={roleStagingVeeva}
        settled={settledStagingVeeva}
        editable={editable}
        onChange={(v) => {
          const name = veevaConnections.find((c) => c.value === v)?.label ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
        }}
      />

      <TextInput
        label="Staging folder"
        description="Inside the vault user's own staging root — the only place they can write. Created if it does not exist. Leave empty to use the root itself."
        placeholder="incoming/protocols"
        value={path}
        disabled={!editable}
        onChange={(e) => onChange("path", e.currentTarget.value)}
      />

      <Divider my={4} label="Files to copy" labelPosition="left" />

      <ConnectionRefSelect
        label="Blob connection"
        placeholder={blobConnections.length ? "Pick a connection" : "No Blob connections set up"}
        data={blobConnections}
        connectionId={blobConnectionId}
        role={roleStagingBlob}
        settled={settledStagingBlob}
        editable={editable}
        onChange={(v) => {
          onChange("fileConnectionId", v);
          onChange("fileContainer", "");
          onChange("filePrefix", "");
        }}
      />

      {blobConnectionId && (
        <Select
          label="Container"
          placeholder="Pick a container"
          data={containers}
          value={container || null}
          disabled={!editable}
          searchable
          onChange={(v) => {
            onChange("fileContainer", v ?? "");
            onChange("filePrefix", "");
          }}
        />
      )}

      {container && (
        <Select
          label="Folder"
          description="Everything under it is copied. Leave empty for the whole container."
          placeholder="The whole container"
          data={folders}
          value={prefix || null}
          disabled={!editable}
          clearable
          searchable
          onChange={(v) => onChange("filePrefix", v ?? "")}
        />
      )}

      <Checkbox
        label="Overwrite files already staged"
        description="On by default: re-running a load should replace its own files rather than fail on them."
        checked={config.overwrite !== false}
        disabled={!editable}
        onChange={(e) => onChange("overwrite", e.currentTarget.checked)}
      />

      {veevaConnectionId && container && (
        <Stack gap={2} mt={4}>
          <Text size="xs" c="dimmed">
            Each file lands at:
          </Text>
          <Code block>{`<staging root>/${path ? `${path}/` : ""}<file name>`}</Code>
        </Stack>
      )}
    </Stack>
  );
}

/** Load-to-Veeva config: pick a Veeva connection and target object, then map
 *  incoming columns → Veeva fields. Both the column list (from the upstream
 *  node) and the field list (from the object's schema) are strict picklists so
 *  a mapping can never point at something that doesn't exist. Leaving the
 *  mapping empty passes rows through unchanged (column names used as fields). */
export function VeevaSinkConfig({
  config,
  editable,
  onChange,
  inputColumns,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  inputColumns: Record<string, string[]>;
}) {
  const getToken = useAccessToken();
  // A node names a connection id or a `@role`; everything here takes an id.
  const {
    connectionId,
    role: roleVeevaSink,
    settled: settledVeevaSink,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const object = String(config.object ?? "");
  // Absent means object records: every graph saved before documents existed was
  // loading object records, and a missing key must not change what it does.
  const dataKind = String(config.dataKind ?? "veeva_object");
  const isDocument = dataKind === "veeva_document";
  // Documents are not a vobject, but Vault does describe them — under this
  // name, through a different metadata endpoint the backend already routes to.
  // So the field picker needs no special case here, only the right target.
  const describeTarget = isDocument ? "documents" : object;
  const action = String(config.action ?? "create");
  const isUpdate = action === "update";
  // Create-or-update, matched on a unique field. What makes a migration safe to
  // run twice: the second run updates what the first one made.
  const isUpsert = action === "upsert" && !isDocument;
  const upsertKey = String(config.upsertKey ?? "");
  const mappings = Array.isArray(config.mappings)
    ? (config.mappings as { column?: string; field?: string }[])
    : [];
  const cols = inputColumns["in-0"] ?? [];

  const [connections, setConnections] = useState<{ value: string; label: string }[]>([]);
  // Where the files live, for creating documents. A separate list from the
  // Veeva connections above: one is where the documents go, the other is where
  // their content comes from.
  const [blobConnections, setBlobConnections] = useState<{ value: string; label: string }[]>([]);
  const [fileContainers, setFileContainers] = useState<string[]>([]);
  const [objects, setObjects] = useState<{ value: string; label: string }[]>([]);
  const [fields, setFields] = useState<string[]>([]);
  // The full definitions, for the mapping sheet: it shows each field's
  // length, type and picklist values, which a list of names cannot.
  const [fieldDefs, setFieldDefs] = useState<FieldDefinition[]>([]);
  // Fields Vault describes as `editable: false`. Some of them (e.g.
  // `file_meta_author__v`) the vault still lets an admin write, so they are
  // offered in their own group and Vault has the final say per row.
  const [readOnlyFields, setReadOnlyFields] = useState<string[]>([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  // Writable fields Vault keeps unique — the only ones an upsert can match on.
  const [uniqueFields, setUniqueFields] = useState<string[]>([]);
  // Reference fields → their target object's sub-fields, so the user can map to
  // e.g. `language__sys.name__v` (a reference lookup) instead of a raw id.
  const [refGroups, setRefGroups] = useState<{ field: string; subs: string[] }[]>([]);
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);

  // Latest values, read inside the async field-load effect without making it a
  // dependency (which would refetch on every keystroke / re-render).
  const mappingsRef = useRef(mappings);
  mappingsRef.current = mappings;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const isUpdateRef = useRef(isUpdate);
  isUpdateRef.current = isUpdate;
  const isDocumentRef = useRef(isDocument);
  isDocumentRef.current = isDocument;
  // Which object we've already auto-seeded required-field rows for, so we do it
  // once per pick and never clobber a saved pipeline's mappings.
  const seededObjectRef = useRef<string>("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await listConnections(token, "veeva")).items;
        if (!cancelled)
          setConnections(items.map((c) => ({ value: c.connection_id, label: c.display_name })));
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (!connectionId) {
      setObjects([]);
      return;
    }
    let cancelled = false;
    setLoadingObjects(true);
    void (async () => {
      try {
        const token = await getToken();
        const items = (await veevaListObjects(token, connectionId)).items;
        if (!cancelled)
          setObjects(
            items.map((o) => ({
              value: o.name,
              label: o.label ? `${o.label} (${o.name})` : o.name,
            })),
          );
      } catch (err) {
        toastApiError("Could not load Veeva objects", err);
      } finally {
        if (!cancelled) setLoadingObjects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  // A node names a connection id or a `@role`; everything here takes an id.
  const {
    connectionId: fileConnectionId,
    role: roleSinkFile,
    settled: settledSinkFile,
  } = useConnectionRef(String(config.fileConnectionId ?? ""));
  // Absent means "whatever this node was already doing": a graph saved with a
  // Blob connection and container was copying from Blob, and a missing key must
  // not change that. Anything else — a new node included — reads the column as
  // staging paths, which is the shape once the files are there.
  const fileSource =
    String(config.fileSource ?? "") ||
    (config.fileConnectionId && config.fileContainer ? "blob" : "staging");
  const needsFiles = isDocument && !isUpdate && fileSource === "blob";

  useEffect(() => {
    if (!needsFiles) return;
    let cancelled = false;
    void (async () => {
      try {
        const items = (await listConnections(await getToken(), "blob")).items;
        if (!cancelled)
          setBlobConnections(items.map((c) => ({ value: c.connection_id, label: c.display_name })));
      } catch (err) {
        toastApiError("Could not load Blob connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, needsFiles]);

  useEffect(() => {
    if (!fileConnectionId) {
      setFileContainers([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const items = (await blobListContainers(await getToken(), fileConnectionId)).items;
        if (!cancelled) setFileContainers(items.map((c) => c.name));
      } catch (err) {
        toastApiError("Could not load containers", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, fileConnectionId]);

  useEffect(() => {
    if (!connectionId || !describeTarget) {
      setFields([]);
      setFieldDefs([]);
      setReadOnlyFields([]);
      setRequiredFields([]);
      setUniqueFields([]);
      setRefGroups([]);
      return;
    }
    let cancelled = false;
    setLoadingFields(true);
    void (async () => {
      try {
        const token = await getToken();
        const defs = (await veevaDescribeObject(token, connectionId, describeTarget)).fields;
        // Only fields you can write to are useful load targets.
        const writable = defs.filter((f) => f.editable !== false);
        const required = writable.filter((f) => f.required).map((f) => f.name);
        if (cancelled) return;
        setFields(writable.map((f) => f.name));
        setFieldDefs(writable);
        setReadOnlyFields(
          defs
            .filter((f) => f.editable === false && f.hidden !== true && f.name !== "id")
            .map((f) => f.name),
        );
        setRequiredFields(required);
        // `id` is unique too, but matching on it is an update, not an upsert.
        setUniqueFields(writable.filter((f) => f.unique && f.name !== "id").map((f) => f.name));
        // On a fresh object pick with no mapping yet, pre-fill one row per
        // required field (field chosen, column left for the user) so the
        // minimum viable load is laid out for them. Never overwrite the
        // mappings a saved pipeline already carries.
        if (
          seededObjectRef.current !== describeTarget &&
          mappingsRef.current.length === 0 &&
          required.length > 0 &&
          !isUpdateRef.current &&
          // Not for documents: see `unsatisfied` below. Pre-filling rows for
          // fields no type requires would have the operator mapping columns
          // they do not have.
          !isDocumentRef.current
        ) {
          onChangeRef.current(
            "mappings",
            required.map((field) => ({ column: "", field })),
          );
        }
        seededObjectRef.current = describeTarget;

        // Reference fields point at another object; describe each target
        // (deduped, in parallel, best-effort) so the field picker can offer
        // reference-lookup sub-fields like `language__sys.name__v`.
        const refFields = writable.filter((f) => f.related_object);
        const targets = [...new Set(refFields.map((f) => f.related_object as string))].slice(0, 30);
        const described = await Promise.allSettled(
          targets.map((o) => veevaDescribeObject(token, connectionId, o)),
        );
        if (cancelled) return;
        const subsByObject = new Map<string, string[]>();
        targets.forEach((o, i) => {
          const r = described[i];
          if (r && r.status === "fulfilled") {
            subsByObject.set(
              o,
              r.value.fields
                .filter((sf) => sf.hidden !== true)
                .map((sf) => sf.name)
                .sort(),
            );
          }
        });
        setRefGroups(
          refFields
            .map((f) => ({
              field: f.name,
              subs: subsByObject.get(f.related_object as string) ?? [],
            }))
            .filter((g) => g.subs.length > 0),
        );
      } catch (err) {
        toastApiError("Could not load fields", err);
      } finally {
        if (!cancelled) setLoadingFields(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, describeTarget]);

  const setRow = (i: number, key: "column" | "field", val: string) =>
    onChange(
      "mappings",
      mappings.map((m, idx) => (idx === i ? { ...m, [key]: val } : m)),
    );
  // Keep a currently-saved value selectable even if it's not in the fetched
  // list (e.g. schema changed) so the picker never silently drops it.
  const colOpts = (cur: string) => [...new Set([...cols, ...(cur ? [cur] : [])])];
  const requiredSet = new Set(requiredFields);
  // Grouped field options: the object's own fields (required marked ★), then
  // one group per reference field with its target object's lookup sub-fields.
  const fieldOpts = (cur: string) => {
    const groups: { group: string; items: { value: string; label: string }[] }[] = [
      // Vault reports `id` as not editable, and it is right — you do not write
      // an id. But an update has to *match* on one, so filtering the field list
      // to editable fields hid the single mapping Update cannot work without.
      // Its own group, and the group heading carries the meaning. An option in
      // a field picker is a **field name**: decorating it with prose turns the
      // list into something to read rather than something to recognise.
      ...(isUpdate ? [{ group: "Match on", items: [{ value: "id", label: "id" }] }] : []),
      // `file` is a column of the manifest, not a field Vault describes, so it
      // will never arrive from describe-object — but it is the one mapping that
      // decides whether a new document has content.
      //
      // The four beside it are the opposite problem: Vault reports them as
      // `editable: false` because they cannot be changed *after* creation, and
      // the field list offers editable fields — so a document create was
      // missing every field Vault requires of it. They are set at creation and
      // only then, which is exactly what this action does.
      ...(isDocument && !isUpdate
        ? [
            {
              group: "The file",
              items: [{ value: "file", label: "file" }],
            },
            {
              group: "Set on create",
              items: DOCUMENT_CREATE_FIELDS.map((name) => ({ value: name, label: name })),
            },
          ]
        : []),
      {
        group: "Fields",
        items: fields
          .filter((name) => !(isUpdate && name === "id"))
          .filter((name) => !(isDocument && !isUpdate && DOCUMENT_CREATE_FIELDS.includes(name)))
          .map((name) => ({
            value: name,
            // No ★ on documents: the flag behind it is a union across every
            // document type, so it would star fields this type never asks for.
            // No ★ in Update mode either: an update needs only `id`.
            label: requiredSet.has(name) && !isDocument && !isUpdate ? `${name} ★` : name,
          })),
      },
      ...(readOnlyFields.length
        ? [
            {
              group: "Read-only in Vault (Vault decides per row)",
              items: readOnlyFields
                .filter(
                  (name) => !(isDocument && !isUpdate && DOCUMENT_CREATE_FIELDS.includes(name)),
                )
                .map((name) => ({ value: name, label: name })),
            },
          ]
        : []),
      ...refGroups.map((g) => ({
        group: `${g.field} → (reference lookup)`,
        items: g.subs.map((sub) => ({ value: `${g.field}.${sub}`, label: `${g.field}.${sub}` })),
      })),
    ];
    const known = new Set(groups.flatMap((grp) => grp.items.map((it) => it.value)));
    if (cur && !known.has(cur))
      groups.push({ group: "Current", items: [{ value: cur, label: cur }] });
    return groups;
  };
  // A required field is satisfied by a mapping with a column set whose field is
  // that field OR a reference-lookup sub-field of it (`field.sub`).
  const isMapped = (f: string, needsColumn: boolean) =>
    mappings.some(
      (m) => (!needsColumn || m.column) && (m.field === f || (m.field ?? "").startsWith(`${f}.`)),
    );
  // In Update mode the ONLY requirement is `id` — every other field is
  // optional, because an update changes just the columns you map. Carrying the
  // Create-mode required-field warnings across would tell the user to fill in
  // fields they are deliberately leaving alone.
  const hasIdMapping = mappings.some(
    (m) => (m.field ?? "").trim() === "id" && (m.column ?? "").trim() !== "",
  );
  const hasFileMapping = mappings.some(
    (m) => (m.field ?? "").trim() === "file" && (m.column ?? "").trim() !== "",
  );
  // Required fields still without a source column — the load would be rejected.
  // Documents are excluded, and this is the whole reason the flag cannot be
  // trusted for them: `metadata/objects/documents/properties` returns the
  // **union** of every document type's fields, and its `required` means
  // "required by some type", not "required by the type you are creating". In
  // this vault it marks `marketing_channel__v` and `notes__v` required — and
  // not one of the 200 documents already in the vault has either set. Warning
  // that "the vault will reject rows without these" was simply false, and the
  // fields a document really cannot be created without are named in the text
  // above the mapping instead.
  const unsatisfied =
    isUpdate || isDocument ? [] : requiredFields.filter((f) => !isMapped(f, true));
  // Required fields not present as a row at all (e.g. the user deleted one).
  const missingRows =
    isUpdate || isDocument ? [] : requiredFields.filter((f) => !isMapped(f, false));

  // What the rest of the form needs before it means anything: a vault, and —
  // for object records — which object.
  const ready = Boolean(connectionId && (isDocument || object));

  return (
    <Stack gap="xs">
      <Select
        label="What am I loading?"
        description="Object records go to their object's bulk endpoint; documents have their own. They are not the same thing in Vault, however alike they look here."
        allowDeselect={false}
        disabled={!editable}
        value={dataKind}
        data={[
          { value: "veeva_object", label: "Veeva object records" },
          { value: "veeva_document", label: "Veeva documents" },
        ]}
        onChange={(v) => {
          const next = v ?? "veeva_object";
          onChange("dataKind", next);
          // The two field sets have nothing in common, so a mapping written for
          // one kind is meaningless for the other. Clearing beats leaving
          // mappings that point at fields the new target does not have.
          onChange("mappings", []);
          onChange("object", "");
        }}
      />

      <ConnectionRefSelect
        label="Veeva connection"
        placeholder={connections.length ? "Pick a connection" : "No Veeva connections set up"}
        data={connections}
        nothingFoundMessage="No Veeva connections"
        connectionId={connectionId}
        role={roleVeevaSink}
        settled={settledVeevaSink}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.value === v)?.label ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          onChange("object", "");
          onChange("mappings", []);
        }}
      />

      {connectionId && !isDocument && (
        <Select
          label="Object"
          placeholder={loadingObjects ? "Loading…" : "Pick a Veeva object"}
          data={objects}
          value={object || null}
          disabled={!editable || loadingObjects}
          searchable
          nothingFoundMessage="No objects"
          onChange={(v) => onChange("object", v ?? "")}
        />
      )}

      {ready && (
        <Select
          label="Action"
          description={
            isDocument && !isUpdate
              ? "Adds new documents. Map file → the column holding each file's path in Blob storage; leave it unmapped to create documents with no content."
              : isDocument
                ? "Changes existing documents. Every row needs the document's id; only the fields you map are changed."
                : isUpdate
                  ? "Changes existing records. Every row needs the record's id; only the fields you map are changed."
                  : isUpsert
                    ? "Matches every row on a unique key: an existing record is updated, anything else is created. Loading the same rows twice updates rather than duplicates — the safe choice for a migration you may run again."
                    : "Adds new records. Vault assigns each id."
          }
          data={[
            {
              value: "create",
              label: isDocument ? "Create new documents" : "Create new records",
            },
            {
              value: "update",
              label: isDocument ? "Update existing documents" : "Update existing records",
            },
            // Vault's upsert (`idParam`) is an object-record API; documents have
            // no equivalent, so the option is not offered for them.
            ...(isDocument ? [] : [{ value: "upsert", label: "Create or update (upsert)" }]),
          ]}
          value={isDocument && action === "upsert" ? "create" : action}
          disabled={!editable}
          allowDeselect={false}
          onChange={(v) => {
            const next = v ?? "create";
            onChange("action", next);
            // Switching to Update: drop the Create-mode required rows that were
            // pre-filled but never given a column (an update needs only `id`),
            // and lay out the `id` row so the one real requirement is visible.
            if (next === "update") {
              const kept = mappings.filter(
                (m) => (m.column ?? "").trim() !== "" || !requiredFields.includes(m.field ?? ""),
              );
              const withId = kept.some((m) => (m.field ?? "").trim() === "id")
                ? kept
                : [{ column: "", field: "id" }, ...kept];
              onChange("mappings", withId);
            }
            // Default the key to the field migrations use for it, when there is
            // one — never overwrite a key the pipeline already chose.
            if (next === "upsert" && !upsertKey && uniqueFields.includes("external_id__v"))
              onChange("upsertKey", "external_id__v");
          }}
        />
      )}

      {ready && isUpsert && (
        <Select
          label="Match on (key field)"
          description="A field Vault keeps unique. Rows whose key already exists update that record; the rest create one. Usually external_id__v, holding each record's id from the source system."
          placeholder={
            uniqueFields.length ? "Pick the key field" : "This object has no unique field"
          }
          data={[...new Set([...uniqueFields, ...(upsertKey ? [upsertKey] : [])])]}
          value={upsertKey || null}
          disabled={!editable || loadingFields}
          searchable
          onChange={(v) => onChange("upsertKey", v ?? "")}
        />
      )}

      {ready && isUpsert && upsertKey && !isMapped(upsertKey, true) && (
        <Alert color="orange" variant="light">
          Map a column to{" "}
          <Text span ff="monospace">
            {upsertKey}
          </Text>{" "}
          — it is what decides, row by row, whether the record already exists. Without it every row
          would be refused.
        </Alert>
      )}

      {ready && isDocument && !isUpdate && (
        <DocumentClassificationPicker
          config={config}
          editable={editable}
          onChange={onChange}
          connectionId={connectionId}
        />
      )}

      {ready && isDocument && !isUpdate && (
        <Stack gap="xs">
          <Select
            label="The files are"
            description="Vault creates documents from a manifest of File Staging paths, so the files have to be there first."
            allowDeselect={false}
            disabled={!editable}
            value={fileSource}
            data={[
              { value: "staging", label: "Already in Veeva FTP (File Staging)" },
              { value: "blob", label: "In Blob storage — copy them to FTP first" },
            ]}
            onChange={(v) => onChange("fileSource", v ?? "staging")}
          />

          {fileSource === "staging" ? (
            <Text size="xs" c="dimmed">
              Map{" "}
              <Text span ff="monospace">
                file
              </Text>{" "}
              to the column holding each file&apos;s staging path — for example{" "}
              <Text span ff="monospace">
                doc/Monitoring_Visit_Report.pdf
              </Text>
              . A path without a leading slash is resolved under this connection&apos;s own staging
              root, so there is no need to paste it onto every row. The Load to Veeva FTP node emits
              exactly this column.
            </Text>
          ) : (
            <Text size="xs" c="dimmed">
              Each row&apos;s file is read from Blob storage and staged in Vault before the
              documents are created — the bytes cross once, and the create call is a list of paths.
            </Text>
          )}

          {fileSource === "blob" && (
            <ConnectionRefSelect
              label="Files are in"
              description="The Blob connection holding the documents to load."
              placeholder={
                blobConnections.length ? "Pick a connection" : "No Blob connections set up"
              }
              data={blobConnections}
              connectionId={fileConnectionId}
              role={roleSinkFile}
              settled={settledSinkFile}
              editable={editable}
              onChange={(v) => {
                onChange("fileConnectionId", v);
                onChange("fileContainer", "");
              }}
            />
          )}
          {fileSource === "blob" && Boolean(config.fileConnectionId) && (
            <Select
              label="Container"
              placeholder="Pick a container"
              data={fileContainers}
              value={String(config.fileContainer ?? "") || null}
              disabled={!editable}
              searchable
              onChange={(v) => onChange("fileContainer", v ?? "")}
            />
          )}
          {!hasFileMapping && (
            <Alert color="blue" variant="light">
              No column is mapped to{" "}
              <Text span ff="monospace">
                file
              </Text>
              , so this creates documents with metadata and no content. Map it to load the files
              themselves.
            </Alert>
          )}
        </Stack>
      )}

      {ready && isUpdate && !hasIdMapping && (
        <Alert color="orange" variant="light">
          Update needs the {isDocument ? "document" : "record"}{" "}
          <Text span ff="monospace">
            id
          </Text>
          . Add a mapping whose Veeva field is{" "}
          <Text span ff="monospace">
            id
          </Text>{" "}
          and point it at the column holding it.
        </Alert>
      )}

      {ready && (
        <Checkbox
          label="Migration mode"
          description="Off unless you need it. Relaxes Vault validation — lets you set system-managed fields and load into any lifecycle state — and Vault refuses the whole load with INSUFFICIENT_ACCESS unless the connection's user holds the record-migration permission. Audited on every load."
          checked={Boolean(config.migrationMode)}
          disabled={!editable}
          onChange={(e) => onChange("migrationMode", e.currentTarget.checked)}
        />
      )}

      {ready && (
        <Stack gap={6}>
          {isDocument && !isUpdate && (
            <Text size="xs" c="dimmed">
              Vault requires{" "}
              <Text span ff="monospace">
                name__v
              </Text>{" "}
              on every new document — map a column to it. Its type, subtype and lifecycle come from
              the pickers above, which offer the labels Vault shows (“Study Documentation”,
              “Report”, “General”) rather than API names. Map a column only where the value
              genuinely differs per row.
            </Text>
          )}
          {!isDocument && fieldDefs.length > 0 && (
            <Group justify="space-between" align="center">
              <Button
                size="compact-sm"
                variant="light"
                leftSection={<IconTable size={14} />}
                disabled={!editable}
                onClick={() => setSheetOpen(true)}
              >
                Open mapping sheet
              </Button>
              <Text size="xs" c="dimmed">
                {mappings.length} of {fieldDefs.length} fields mapped
              </Text>
            </Group>
          )}

          <MappingSheet
            opened={sheetOpen}
            onClose={() => setSheetOpen(false)}
            fields={fieldDefs}
            columns={cols}
            mappings={mappings as Mapping[]}
            onChange={(next) => onChange("mappings", next)}
            objectLabel={object || "the target"}
          />

          <Text size="xs" c="dimmed">
            Map columns → Veeva fields.{" "}
            {isUpdate ? (
              <>
                Map{" "}
                <Text span ff="monospace">
                  id
                </Text>{" "}
                plus only the fields you want to change — anything you leave out stays as it is in
                the vault.
              </>
            ) : requiredFields.length > 0 && !isDocument ? (
              <>
                Required fields are marked <Text span>★</Text> and pre-filled — just pick a column
                for each.
              </>
            ) : (
              "Leave empty to use the column names as field names."
            )}{" "}
            For a reference field (e.g. a lookup), pick its sub-field like{" "}
            <Text span ff="monospace">
              language__sys.name__v
            </Text>{" "}
            to match on a name instead of an id.
          </Text>
          {cols.length === 0 && (
            <Text size="xs" c="dimmed">
              No input columns detected yet — Test/run the upstream source (a SQL Query step also
              hides column names). You can still type a column name.
            </Text>
          )}
          {mappings.map((m, i) => (
            <Group key={i} gap={4} wrap="nowrap" align="flex-end">
              <Autocomplete
                label={i === 0 ? "Column" : undefined}
                placeholder={cols.length ? "column" : "type a column name"}
                data={colOpts(String(m.column ?? ""))}
                value={String(m.column ?? "")}
                disabled={!editable}
                style={{ flex: 1 }}
                onChange={(v) => setRow(i, "column", v)}
              />
              <Text pb={6}>→</Text>
              <Select
                label={i === 0 ? "Veeva field" : undefined}
                placeholder={loadingFields ? "Loading…" : "field"}
                data={fieldOpts(String(m.field ?? ""))}
                value={String(m.field ?? "") || null}
                disabled={!editable || loadingFields}
                searchable
                style={{ flex: 1 }}
                onChange={(v) => setRow(i, "field", v ?? "")}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                disabled={!editable}
                aria-label="Remove mapping"
                onClick={() =>
                  onChange(
                    "mappings",
                    mappings.filter((_, idx) => idx !== i),
                  )
                }
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
          <Group gap={6}>
            <Button
              size="compact-xs"
              variant="light"
              disabled={!editable}
              onClick={() => onChange("mappings", [...mappings, { column: "", field: "" }])}
            >
              Add mapping
            </Button>
            {missingRows.length > 0 && (
              <Button
                size="compact-xs"
                variant="light"
                color="orange"
                disabled={!editable}
                onClick={() =>
                  onChange("mappings", [
                    ...mappings,
                    ...missingRows.map((field) => ({ column: "", field })),
                  ])
                }
              >
                Add required fields ({missingRows.length})
              </Button>
            )}
          </Group>
          {unsatisfied.length > 0 && (
            <Text size="xs" c="orange">
              ★ Still needs a column: {unsatisfied.join(", ")}. The vault will reject rows without
              these.
            </Text>
          )}
          <Text size="xs" c="dimmed">
            {isDocument ? "Each incoming row is one document" : "Each incoming row is one "}
            {!isDocument && (
              <Text span ff="monospace">
                {object}
              </Text>
            )}
            {!isDocument && " record"}. A row the vault refuses is reported, not fatal — see the
            load report. Loads go in batches of 500; if one fails part-way the node fails and says
            which rows landed, which may have, and which were never sent.
          </Text>
        </Stack>
      )}

      {ready && isDocument && isUpdate && (
        <Stack gap={6}>
          <Checkbox
            label="Classify (move documents out of the Inbox)"
            description="Sends each document with its type, subtype, classification, lifecycle and every other mapped field in one call — what Classify does in Vault. The document leaves the Inbox and lands in its lifecycle's starting state (In Progress for General). Without this, a document in the Inbox gets its fields but stays Unclassified. Needs type__v on every row."
            checked={Boolean(config.reclassify)}
            disabled={!editable}
            onChange={(e) => onChange("reclassify", e.currentTarget.checked)}
          />
          {Boolean(config.reclassify) && (
            <TextInput
              label="Lifecycle"
              description="Optional. Used when a row has no lifecycle__v column; if left empty, each type's only lifecycle is used (e.g. General)."
              placeholder="From lifecycle__v, or the type's only lifecycle"
              value={String(config.reclassifyLifecycle ?? "")}
              disabled={!editable}
              onChange={(e) => onChange("reclassifyLifecycle", e.currentTarget.value)}
            />
          )}
        </Stack>
      )}

      {ready && isDocument && (
        <Stack gap={6}>
          <Checkbox
            label="Update status"
            description="After the load, each document Vault accepted is moved to the status below through its lifecycle's own state-change action (what Change State does in Vault), and read back to confirm. A document already there is left alone. A status__v column, if mapped, sets the status per row. Problems are reported in the load report's state columns."
            checked={Boolean(config.updateStatus ?? config.moveToInProgress)}
            disabled={!editable}
            onChange={(e) => onChange("updateStatus", e.currentTarget.checked)}
          />
          {Boolean(config.updateStatus ?? config.moveToInProgress) && (
            <TextInput
              label="Status"
              value={String(config.targetStatus ?? "") || "In Progress"}
              disabled={!editable}
              onChange={(e) => onChange("targetStatus", e.currentTarget.value)}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
}

/** Veeva Source config: pick a Veeva connection, build/edit VQL (embedded
 *  VeevaQueryBuilder), and Test to preview the result table inline in the node.
 *  Test is a read-only preview — it does not run the pipeline.
 *
 *  **Reads only.** Saving what a query returns is the Save-to-Blob target's
 *  job; this node no longer carries download toggles or a Blob destination. */
function VeevaQueryConfig({
  config,
  editable,
  onChange,
  onColumns,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  onColumns?: (cols: string[]) => void;
}) {
  const getToken = useAccessToken();
  // A node names a connection id or a `@role`; everything here takes an id.
  const {
    connectionId,
    role: roleVeevaSource,
    settled: settledVeevaSource,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const vql = String(config.vql ?? "");
  const [connections, setConnections] = useState<{ value: string; label: string }[]>([]);
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState<{
    rows: Record<string, unknown>[];
    total: number | null;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const veeva = await listConnections(token, "veeva");
        if (cancelled) return;
        setConnections(veeva.items.map((c) => ({ value: c.connection_id, label: c.display_name })));
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const resolveLabels = Boolean(config.resolveLabels);

  const onTest = async () => {
    if (!connectionId || !vql.trim()) return;
    setTesting(true);
    try {
      const token = await getToken();
      const res = await veevaQuery(token, connectionId, vql, resolveLabels);
      const rows = res.records as Record<string, unknown>[];
      const cols = columnsOf(rows);
      setPreview({ rows, total: res.total });
      onColumns?.(cols); // immediate (this session)
      onChange("previewColumns", cols); // persisted → survives reload, feeds downstream pickers
    } catch (err) {
      toastApiError("Test failed", err);
    } finally {
      setTesting(false);
    }
  };

  const columns = preview ? [...new Set(preview.rows.flatMap((r) => Object.keys(r)))] : [];
  const shown = preview?.rows.slice(0, 20) ?? [];

  return (
    <Stack gap="xs">
      <ConnectionRefSelect
        label="Veeva connection"
        placeholder={connections.length ? "Pick a connection" : "No Veeva connections set up"}
        data={connections}
        nothingFoundMessage="No Veeva connections"
        connectionId={connectionId}
        role={roleVeevaSource}
        settled={settledVeevaSource}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.value === v)?.label ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          setPreview(null);
        }}
      />

      {connectionId && (
        <>
          <VeevaQueryBuilder
            connectionId={connectionId}
            // Ignore the builder's empty emit on mount so it never wipes a saved
            // VQL; only a real (non-empty) query updates the field.
            onVqlChange={(v) => {
              if (v.trim()) onChange("vql", v);
            }}
          />
          <Textarea
            label="VQL"
            description="Built above — edit by hand if you like."
            autosize
            minRows={2}
            value={vql}
            disabled={!editable}
            onChange={(e) => onChange("vql", e.currentTarget.value)}
          />
          <Checkbox
            label="Also fetch labels for reference fields"
            description="A reference column holds an id — created_by__v is 14663077. This adds a <field>__label column beside it with the name, keeping the id, because the id is what a load back into Vault needs."
            checked={resolveLabels}
            disabled={!editable}
            onChange={(e) => {
              onChange("resolveLabels", e.currentTarget.checked);
              setPreview(null);
            }}
          />

          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPlay size={14} />}
            onClick={() => void onTest()}
            loading={testing}
            disabled={!vql.trim()}
          >
            Test — preview data
          </Button>

          {/* Saving lives on the Save-to-Blob target now: a source reads, a
              target writes. This node used to carry four download toggles and a
              Blob destination, which meant you could not read a query without
              also re-saving it. */}
        </>
      )}

      {preview && (
        <Stack gap={4} mt="xs">
          <Text size="xs" tt="uppercase" fw={600} c="dimmed">
            Preview
          </Text>
          <Text size="xs" c="dimmed">
            {(preview.total ?? preview.rows.length).toLocaleString()} row(s)
            {preview.rows.length > shown.length ? ` · showing first ${shown.length}` : ""}
          </Text>
          {shown.length === 0 ? (
            <Text size="xs" c="dimmed">
              No rows matched.
            </Text>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table striped withTableBorder verticalSpacing={4} fz="xs">
                <Table.Thead>
                  <Table.Tr>
                    {columns.map((c) => (
                      <Table.Th key={c}>{c}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {shown.map((r, i) => (
                    <Table.Tr key={i}>
                      {columns.map((c) => (
                        <Table.Td key={c}>{cellText(r[c])}</Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function runDuration(run: RunSummaryOut): string | null {
  if (!run.started_at_utc || !run.ended_at_utc) return null;
  const ms = new Date(run.ended_at_utc).getTime() - new Date(run.started_at_utc).getTime();
  if (ms < 0) return null;
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(2)} s`;
}

/** Render a timestamp in UTC with a "UTC" suffix, so every time in the app is in
 *  one zone and matches the audit trail (which is UTC). Times are stored UTC. */
function formatUtc(iso: string): string {
  return `${new Date(iso).toLocaleString(undefined, { timeZone: "UTC" })} UTC`;
}

/** The Execution tab: run history (left) + the selected run's result (right) —
 *  n8n's Executions view. The Workflow tab's canvas still colours nodes live;
 *  this tab is the persisted record: status, timing, and Display output. */
function ExecutionPanel({
  runs,
  selectedRunId,
  onSelect,
  onRefresh,
  pipelineId,
  nodeLabelById,
}: {
  runs: RunSummaryOut[];
  selectedRunId: string | null;
  onSelect: (runId: string) => void;
  onRefresh: () => void;
  pipelineId: string;
  nodeLabelById: Record<string, string>;
}) {
  if (runs.length === 0) {
    return (
      <Stack align="center" gap="xs" p="xl">
        <Text fw={600}>No runs yet</Text>
        <Text size="sm" c="dimmed">
          Press Run on the Workflow tab to execute this pipeline. Runs appear here with their result
          and history.
        </Text>
      </Stack>
    );
  }
  // Safe: guarded by the runs.length === 0 early-return above.
  const selected = runs.find((r) => r.run_id === selectedRunId) ?? runs[0]!;
  return (
    <Group align="stretch" gap="sm" wrap="nowrap" style={{ height: "100%" }}>
      <Paper withBorder radius="md" style={{ width: 280, overflowY: "auto", flex: "0 0 auto" }}>
        <Group justify="space-between" p="xs" pb={4}>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed">
            Run history
          </Text>
          <ActionIcon variant="subtle" size="sm" onClick={onRefresh} aria-label="Refresh runs">
            <IconRefresh size={14} />
          </ActionIcon>
        </Group>
        <Stack gap={6} p="xs" pt={0}>
          {runs.map((r) => (
            <Paper
              key={r.run_id}
              withBorder
              radius="sm"
              p="xs"
              onClick={() => onSelect(r.run_id)}
              style={{
                cursor: "pointer",
                borderColor:
                  r.run_id === selected.run_id ? "var(--mantine-primary-color-filled)" : undefined,
                borderWidth: r.run_id === selected.run_id ? 2 : undefined,
              }}
            >
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Badge size="xs" color={statusColor(r.status)} variant="light">
                  {r.status}
                </Badge>
                <Badge size="xs" variant="outline" tt="lowercase">
                  {r.trigger}
                </Badge>
              </Group>
              <Text size="xs" c="dimmed" mt={4}>
                {r.started_at_utc ? formatUtc(r.started_at_utc) : "—"}
              </Text>
            </Paper>
          ))}
        </Stack>
      </Paper>
      <Paper withBorder radius="md" p="md" style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
        <RunDetail run={selected} pipelineId={pipelineId} nodeLabelById={nodeLabelById} />
      </Paper>
    </Group>
  );
}

/** Scheduler lateness: actual start minus intended fire time. */
function lateness(run: RunSummaryOut): string | null {
  if (!run.scheduled_for_utc || !run.started_at_utc) return null;
  const ms = new Date(run.started_at_utc).getTime() - new Date(run.scheduled_for_utc).getTime();
  if (ms < 250) return "on time";
  return ms < 1000 ? `${ms} ms late` : `${(ms / 1000).toFixed(1)} s late`;
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <Text size="xs" c="dimmed" style={{ width: 120, flex: "0 0 auto" }}>
        {label}
      </Text>
      <Text component="div" size="xs" style={{ minWidth: 0, wordBreak: "break-word" }}>
        {children}
      </Text>
    </Group>
  );
}

function RunDetail({
  run,
  pipelineId,
  nodeLabelById,
}: {
  run: RunSummaryOut;
  pipelineId: string;
  nodeLabelById: Record<string, string>;
}) {
  const byNode = new Map<string, RunSummaryOut["displays"]>();
  for (const d of run.displays) {
    const arr = byNode.get(d.node_id) ?? [];
    arr.push(d);
    byNode.set(d.node_id, arr);
  }
  const duration = runDuration(run);
  const late = lateness(run);
  const nodes = [...(run.nodes ?? [])].sort((a, b) => a.order - b.order);
  return (
    <Stack gap="sm">
      <Group gap="xs">
        <Badge color={statusColor(run.status)} variant="filled">
          {run.status}
        </Badge>
        <Badge variant="outline" tt="lowercase">
          {run.trigger}
        </Badge>
        {run.lifecycle_state && (
          <Badge variant="light" tt="capitalize">
            {run.lifecycle_state}
          </Badge>
        )}
      </Group>

      {/* Reproducibility & context — what ran, in what state, launched by whom. */}
      <Stack gap={2}>
        {run.project_name && <MetaRow label="Project">{run.project_name}</MetaRow>}
        <MetaRow label="Pipeline">{run.pipeline_name ?? pipelineId}</MetaRow>
        <MetaRow label="Execution started by">
          {run.actor_user_id === "system" ? (
            "System (scheduler)"
          ) : (
            <Stack gap={0}>
              {run.actor_display_name && <span>{run.actor_display_name}</span>}
              <span>{run.actor_email ?? "(email unavailable)"}</span>
              {run.actor_user_id && (
                <Text span size="xs" c="dimmed" ff="monospace">
                  {run.actor_user_id}
                </Text>
              )}
            </Stack>
          )}
        </MetaRow>
        <MetaRow label="Started">
          {run.started_at_utc ? formatUtc(run.started_at_utc) : "—"}
          {duration ? ` · took ${duration}` : ""}
        </MetaRow>
        {run.scheduled_for_utc && (
          <MetaRow label="Scheduled">
            {formatUtc(run.scheduled_for_utc)}
            {late ? ` · ${late}` : ""}
          </MetaRow>
        )}
        <MetaRow label="Version">
          {run.version_no != null ? (
            <>
              v{run.version_no}
              {run.version_created_at_utc && (
                <Text span c="dimmed">
                  {` · saved ${formatUtc(run.version_created_at_utc)}`}
                </Text>
              )}
            </>
          ) : (
            "—"
          )}
        </MetaRow>
        {run.graph_hash && (
          <MetaRow label="Graph">
            <Text span c="dimmed" ff="monospace">
              {run.graph_hash.slice(0, 12)}
            </Text>
          </MetaRow>
        )}
        {run.request_id && (
          <MetaRow label="Correlation">
            <Text span ff="monospace" c="dimmed">
              {run.request_id}
            </Text>
          </MetaRow>
        )}
      </Stack>

      {run.error && (
        <Alert variant="light" color="red" title="Run failed">
          {run.error}
        </Alert>
      )}

      {/* Run log — per-node status, order, and timing (the debugging surface). */}
      <Divider label="Run log" labelPosition="left" />
      {nodes.length === 0 ? (
        <Text size="sm" c="dimmed">
          No nodes ran.
        </Text>
      ) : (
        <Table striped withTableBorder verticalSpacing={4} fz="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={32}>#</Table.Th>
              <Table.Th>Node</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Duration</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {nodes.map((n) => (
              <Table.Tr key={n.id}>
                <Table.Td>{n.order + 1}</Table.Td>
                <Table.Td>{nodeLabelById[n.id] ?? n.id.slice(0, 8)}</Table.Td>
                <Table.Td>
                  <Badge size="xs" color={statusColor(n.status)} variant="light">
                    {n.status}
                  </Badge>
                  {n.error ? (
                    <Text size="xs" c="red.6">
                      {n.error}
                    </Text>
                  ) : null}
                </Table.Td>
                <Table.Td>
                  {n.duration_ms != null ? `${n.duration_ms.toFixed(1)} ms` : "—"}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Divider label="Display output" labelPosition="left" />
      {byNode.size === 0 ? (
        <Text size="sm" c="dimmed">
          This run produced no Display output. Add a Display node to see data here.
        </Text>
      ) : (
        [...byNode.entries()].map(([nodeId, items]) => (
          <Stack key={nodeId} gap={4}>
            <Text size="xs" fw={600}>
              {nodeLabelById[nodeId] ?? nodeId.slice(0, 8)}
            </Text>
            {items.map((r) => (
              <Stack key={`${nodeId}-${r.port}`} gap={2}>
                <Text size="xs" c="dimmed">
                  Input {Number(String(r.port).replace("in-", "")) + 1}
                </Text>
                <DataView data={r.data} />
              </Stack>
            ))}
          </Stack>
        ))
      )}
    </Stack>
  );
}

export function PipelineCanvas() {
  const { projectId = "", pipelineId = "" } = useParams();
  return (
    <ReactFlowProvider>
      <CanvasInner projectId={projectId} pipelineId={pipelineId} />
    </ReactFlowProvider>
  );
}
